import React, { useState, useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import "react-datepicker/dist/react-datepicker.css";
import "./Modal.css";
import "./PromptModal.css";
import "./TripAI.css";
import "./BuildMyTripWizard.css";
import AlertModal from "./AlertModal";
import DestinationsScreen from "./DestinationsScreen";
import PreferencesScreen from "./PreferencesScreen";
import { effectiveDestinations } from "./ScheduleDestinationsFlow";
import AISearch from "./AISearch";
import Common, { AICategories as DEFAULT_AI_CATEGORIES, AIBaseCategories as DEFAULT_AI_BASE_CATEGORIES } from "../../Common/Common";
import { overlapError, tripLengthError, cleanDestination, inTripSchedule, removeFromSchedule, replacedEvents } from "../../Common/DestinationValidation";
import Places from "../../Models/Places";
import Place, { Place_Source } from "../../Models/Place";
import Schedule from "../../Models/Schedule";
import Appointment from "../../Models/Event";
import GooglePlaces, { mapGoogleTypesToCategoryId } from "../../Models/GooglePlaces";
import PlaceMenu from "../../CommonWeb/PlaceMenu";
import { DateTime } from "luxon";
import { geocode, setKey, RequestType } from "react-geocode";
import { citiesByCountry } from "../../Data/Cities";
import { slice1Actions } from "../../store/reducer";
import { isPlaceholderDoc } from "../../Common/exportTripHTML";
import { getCachedSearchResults, setCachedSearchResults } from "../../Common/SessionPlaceSearchCache";
import { loadGuidePlaces, canCopyFrom, cityKey, placesForTarget } from "../../Common/GuidePlaceSource";
import DataBase, { logAnalyticsEvent } from "../../dataBase/liquidGuides";
import { planWindows, windowFor, buildAIDaySchedule, applyAIBestTime } from "../../Common/aiDaySchedule";
import { buildPlacesQuestion, askForPlaces } from "../../Common/aiTripPlaces";
import VoiceTripIntentFlow from "./VoiceTripIntentFlow";
import { eligibleGuidePlaces, suggestDestinations, assignToDestinations, shareRepeatVisits } from "../../Common/placesToDestinations";
import PlacesReviewModal from "./PlacesReviewModal";
import { takeDailyLimitNotice, clearDailyLimitNotice, showLimitNotice, LIMIT_MESSAGES, openPartnerOffers } from "../../Common/GateLimit";
import GuideDownload from "./GuideDownload/GuideDownload";
import schedulePlacesInOrder from "../../Common/schedulePlacesInOrder";

// Only destinations with a real place to search for get scheduled here — a
// placeholder day (city: "", "Sea Day"/"Travel Day" — see DestinationsScreen.js)
// has nothing to search for. A destination with a real place but a
// zero-length date range (dateStart === dateEnd, e.g. a brief geocoded stop
// like "Narita Airport" the user wants kept on the map without triggering an
// attraction search) is excluded the same way — there's no time window to
// fill with found places. Both still occupy their date range on the schedule
// implicitly; this wizard just doesn't process them as a leg.
// effectiveDestinations() is the single canonical source shared with
// DestinationsScreen.js/TripAI.js — see ScheduleDestinationsFlow.js.
const placedDestinationsFromGuide = () =>
  effectiveDestinations().filter((d) => d.city && !(d.dateStart && d.dateEnd && d.dateStart.equals(d.dateEnd)));

// Single setup dialog — destination, dates, and every option (schedule
// target, places source, pace, etc.) collected in one TripAI-styled shell
// (same tai-* classes/markup as TripAI.js's own destination cards) instead
// of a chain of separate full-screen steps. Only one destination for now —
// multi-destination (via Explore Destinations, like TripAI's own) is a
// planned follow-up once this single-destination shell is proven out.
const STEP_SETUP = 2;
const STEP_AI_SEARCH = 4;
const STEP_PICKING = 5;
const STEP_ENRICHING = 8;
const NEW_SCHEDULE_VALUE = "__new__";
// "Overwrite <schedule>" (2026-10-16): that schedule's events are removed,
// then the trip is built into it — as the old Build Trip from Places dialog's
// Replace and Build Trip with AI's "Overwrite Main" did.
const OVERWRITE_PREFIX = "__overwrite__:";
// parseNewAPIPlace sets Photo/Photo_m to this generic placeholder for every
// Google-sourced place (the field mask deliberately excludes real Places
// photos — see google_places_new_search.php). A plain `place.Photo` truthy
// check therefore matches EVERY place, real photo or not — enrichPlacePhoto's
// "already has one" guard and the cover-photo pick below both need to treat
// this placeholder as "no real photo yet", or they silently no-op/always pick
// the first place regardless of whether it ever got a real photo. Found
// 2026-08-27 testing "let me pick" via Build My Trip — no guide ever got a
// real hero photo because of this.
const ILLUSTRATED_PLACEHOLDER = "https://andiamotravelapp.com/Images/App/IllustratedImage.jpg";
// A live Google Places (New) Photo Media URL should never count as a "real"
// photo either, even though nothing in this codebase writes one to
// place.Photo/Photo_m anymore — a stale, pre-fix cached guide getting saved
// again can still bring one back (see the cover-image checks below, which
// hit exactly this live on 2026-08-30). Rejecting it here too means the
// auto-pick fallback itself can never re-select a leaked billing URL as a
// place's "real" photo in the first place.
const GOOGLE_PLACES_PHOTO_PATTERN = /places\.googleapis\.com\/v1\/.*\/media/i;
const hasRealPhoto = (place) =>
  (place.Photo && place.Photo !== ILLUSTRATED_PLACEHOLDER && !GOOGLE_PLACES_PHOTO_PATTERN.test(place.Photo)) ||
  (place.Photo_m && place.Photo_m !== ILLUSTRATED_PLACEHOLDER && !GOOGLE_PLACES_PHOTO_PATTERN.test(place.Photo_m));
// A brand-new guide (the wizard's "new guide" path, MobileBottomNavBar.js's
// createLocalGuide) carries Guide.js's placeholder page text, not "" — so
// runNextDestination's empty-document check uses isPlaceholderDoc
// (Common/exportTripHTML.js), which knows every version of that text.
// Reached instead of STEP_AI_SEARCH when "Places source" is set to Google —
// sources real places from a live search instead of having an LLM invent
// them.
const STEP_GOOGLE_SEARCH = 11;

// Shared with DestinationsScreen.js's own "Auto-Build"/"Let Me Pick" offer
// and BuildScheduleDialog.js — see Common.PACE_OPTIONS's comment.
const PACE_OPTIONS = Common.PACE_OPTIONS;

// Name+rounded-coordinate identity for a place — same convention
// GooglePlaces.js's searchCityAttractions uses to dedupe within one search
// call, reused here to dedupe against the guide's own places.
const placeKey = (p) => `${(p.Name || "").toLowerCase()}|${(p.Lat || 0).toFixed(3)}|${(p.Longi || 0).toFixed(3)}`;

// In-memory key only (destinationPlaceIdsRef) — doesn't need to match
// SessionPlaceSearchCache's own sessionStorage key format, just be
// internally consistent within this component's own run.
const destinationCacheKey = (city, country) => `${(city || "").trim().toLowerCase()}|${(country || "").trim().toLowerCase()}`;

// Keys of every place already scheduled as an event under `scheduleName`
// specifically — not the whole guide's places — checked fresh at each
// destination's search, not cached. Scoped to the schedule (rather than the
// guide) deliberately: two genuinely separate trips sharing one guide (e.g.
// "Italy 2024" and "Italy 2026," both revisiting Rome years apart) may well
// want the same great restaurant again, so a guide-wide check would wrongly
// suppress that. This still covers a repeat visit to the same city within
// one multi-destination run (e.g. Aomori on day 1 and again on day 3, both
// landing in the same schedule), and covers a later "Build My Trip" run
// too, but only when it explicitly targets ("Add to X") the same schedule
// — creating a new schedule each run, the default, starts this fresh.
const scheduledPlaceKeysForSchedule = (scheduleName) => {
  const keys = new Set();
  if (!scheduleName) return keys;
  (window.guide_obj?.schedule?.events || []).forEach((e) => {
    if (e.Itinerary !== scheduleName) return;
    String(e.Places || "").split(",").map((id) => id.trim()).filter(Boolean).forEach((id) => {
      const place = window.guide_obj.places.getPlace(id);
      if (place) keys.add(placeKey(place));
    });
  });
  return keys;
};

// "Build My Trip" — guided on-ramp for a guide: one setup dialog (destination,
// dates, schedule timing, pace, places source — AI-suggested or real Google
// Places, and whether to review places or add everything automatically),
// then search/build/enrich according to those choices with no further
// prompts unless the user asked to review places first. Runs against the
// currently open guide if it already has places (letting it add a second
// leg/day to an existing trip), or a freshly-blanked one otherwise. The user
// can back out to plain manual mode at any step.
const BuildMyTripWizard = (props) => {
  const dispatch = useDispatch();
  let user_info = useSelector((state) => state.user_info);
  const guide_meta = useSelector((state) => state.guide_meta);
  let AppSettings = useSelector((state) => state.AppSettings);
  let GuideSettings = useSelector((state) => state.GuideSettings);
  let MobileMode = useSelector((state) => state.MobileMode);
  const GuideHasUnsavedChanges = useSelector((state) => state.GuideHasUnsavedChanges);
  let SearchResultsPlacesList = useSelector((state) => state.SearchResultsPlacesList);

  const [step, setStep] = useState(STEP_SETUP);
  // Fires once when opened via the command palette's "Build Trip with AI"
  // entry (props.autoOpenAI) — immediately hands off to Build Trip with AI via
  // the same onSwitchToAI path the in-wizard link uses, so that one
  // still-searchable command doesn't need its own separate modal. Guarded by
  // a ref (not state) so it can't refire on unrelated re-renders once it's
  // already switched away. 2026-10-04.
  // eslint-disable-next-line no-unused-vars
  const { autoOpenAI, onSwitchToAI } = props;
  // 2026-10-19 (Build My Trip stage 4): "Build Trip with AI" now means this
  // wizard with the AI source chosen — same AI on web and mobile, faster,
  // scheduled around opening hours — instead of the separate TripAI dialog.
  // Old hand-off kept commented in case we go back:
  //   onSwitchToAI?.();
  // The AI tile itself is chosen by the open/reset effect below
  // (props.autoOpenAI), which runs after this one and would undo it here.
  // Read-only here — the guide's persisted destinations (see
  // DestinationsScreen.js) are the single source of truth. Only entries with
  // a real place (placedDestinationsFromGuide) are shown/processed; a
  // placeholder day is edited/added exclusively via "Edit Destinations"
  // below, never in this wizard.
  //
  // props.initialDestinations (ImportItineraryImageModal.js) — shown here
  // for review/setup, but NOT written onto window.guide_obj.destinations
  // until startBuild() actually commits (see its own comment).
  // ImportItineraryImageModal used to write them onto the guide
  // immediately, before the user ever reached this wizard — closing it
  // without completing still left the ORIGINAL guide's destinations
  // overwritten, a real data-loss bug if the guide was then saved. Matches
  // mobile's identical fix. Found 2026-09-28.
  const [destinations, setDestinations] = useState(() => props.initialDestinations || placedDestinationsFromGuide());
  const [showDestinationsScreen, setShowDestinationsScreen] = useState(false);
  // Guide.aiPreferences, edited via PreferencesScreen.js — same field/UI
  // TripAI.js's "Build Trip with AI" already uses. The "AI suggestions" place
  // source previously never surfaced or read this at all, despite its own
  // label promising "customize to your preferences" — fixed 2026-08-29.
  const [showPreferences, setShowPreferences] = useState(false);
  const [destError, setDestError] = useState("");
  const [destValidationError, setDestValidationError] = useState("");
  // Defaults to "Main" (not NEW_SCHEDULE_VALUE) — matches mobile's own
  // BuildMyTripWizard.js and the same rule applied to every guide-creation
  // wizard: whenever "Add to this guide" ends up selected, the schedule
  // sub-choice defaults to Main rather than silently proposing a new one.
  // props.initialScheduleTarget: e.g. NEW_SCHEDULE_VALUE from Import
  // Confirmation's AI-trip offer, so the imported flights/hotels in "Main"
  // are left alone (2026-10-19).
  const [scheduleTarget, setScheduleTarget] = useState(props.initialScheduleTarget || "Main");
  const [newScheduleName, setNewScheduleName] = useState("");
  // Guide-level "where should this trip go?" choice — matches TripAI's own
  // new/add/replace radio exactly (see TripAI.js's whereToGo/selectWhereToGo/
  // ConfirmCreate), so the two "Create a trip" entry points behave the same
  // way instead of Build My Trip silently auto-deciding and warning before
  // the setup dialog even appears.
  // Defaults to "new" (the common case: Build My Trip opened cold, nothing
  // worth keeping yet). A caller that already put real content on the
  // current guide before opening this — e.g. DestinationsScreen.js's "Find
  // Places" offer, reached right after the user just set destinations on
  // THIS guide — passes initialGuideTarget="add" so Create doesn't silently
  // spin up a brand-new guide and discard what was just saved.
  const [guideTarget, setGuideTarget] = useState(props.initialGuideTarget || "new");
  // "Skip destinations already in this trip" (2026-10-22): a visible choice
  // instead of a silent rule; untick to build them again.
  const [skipBuilt, setSkipBuilt] = useState(true); // ticked by default, every source
  // Already in this trip (✓) = built in an earlier run AND still in the
  // schedule being added to (2026-10-23, see inTripSchedule). A new guide,
  // a new schedule or an overwritten one has nothing in it yet, so nothing
  // counts there.
  const inTripScheduleName = guideTarget === "add" && scheduleTarget !== NEW_SCHEDULE_VALUE && !scheduleTarget.startsWith(OVERWRITE_PREFIX) ? scheduleTarget : null;
  const isInTrip = (d) => inTripSchedule(d, window.guide_obj, inTripScheduleName);
  // Redo one destination (2026-10-23): tapping its ✓ builds it again into
  // its own days, replacing what's there (removeFromSchedule), while the
  // others stay skipped. Unticking "Skip" redoes every ✓ the same way —
  // before, it added a second set of events on the same days.
  const [redoIds, setRedoIds] = useState([]);
  const willRedo = (d) => isInTrip(d) && (!skipBuilt || redoIds.includes(d.id));
  // A destination whose city or dates changed after it was built (builtAs,
  // see editDestination): what it replaces in the schedule, for its label.
  const replacesLabel = (d) => {
    if (!d.builtAs || !replacedEvents(d, window.guide_obj, inTripScheduleName).length) return "";
    return d.builtAs.city !== d.city ? `Replaces ${d.builtAs.city}` : "New dates";
  };
  const toggleRedo = (id) => setRedoIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  // What a run builds: new destinations, plus the ones being redone — at
  // their saved dates (where their events are), even when the others were
  // moved to follow the chosen schedule.
  const destinationsToBuild = () => destinations
    .filter((d) => d.country && d.city && (!isInTrip(d) || willRedo(d)))
    .map((d) => {
      if (!willRedo(d)) return d;
      const saved = (window.guide_obj.destinations || []).find((x) => x.id === d.id);
      return saved ? { ...d, dateStart: saved.dateStart, dateEnd: saved.dateEnd } : d;
    });
  const [newGuideName, setNewGuideName] = useState("");
  // Deferred create/replace confirmation — set only at Create-time (never
  // while just picking the radio), same as TripAI's AlertModalShow. null when
  // no alert is showing. { title, message, buttons } — buttons follow
  // AlertModal's own AlertModalButtons shape.
  const [guideActionAlert, setGuideActionAlert] = useState(null);
  // Real places (Google) is the default — it's faster and its schedules come
  // with zero opening-hours conflicts, vs. AI-suggested places which can
  // still land on closed venues. AI suggestions stay available as the
  // "customize with AI" alternative.
  const [placeSource, setPlaceSource] = useState(props.autoOpenAI ? "ai" : "google");
  const [showAdvanced, setShowAdvanced] = useState(false);
  // An important, always-visible choice, not a buried preference — how a
  // trip's places get chosen materially changes what "Create" does (auto-
  // add-and-schedule vs. hand off to the Places screen for review), so it
  // lives in its own primary section, not under "Show advanced options".
  const [pickPlacesAuto, setPickPlacesAuto] = useState(true);
  // "From existing guides" — the third answer to "How should places be
  // chosen?" (2026-10-12). Each destination gets its places from the guide(s)
  // picked for it instead of a Google/AI search; everything else about the
  // run is unchanged. A destination with no guide picked searches as usual.
  // guideSources: { [cityKey(city, country)]: [{ guide_info_id, guide_name }] }.
  const [fromGuides, setFromGuides] = useState(!!props.initialFromGuides);
  // Today's search limit was reached (see the search-error dialog below);
  // switchToGuidesRef makes that dialog's close keep the wizard open when
  // "Use existing guides" was chosen.
  const [searchLimitHit, setSearchLimitHit] = useState(false);
  const switchToGuidesRef = useRef(false);
  const [guideSources, setGuideSources] = useState({});
  // Every guide this user could take places from (their own + Featured),
  // loaded once when the option is first chosen — only to show, per
  // destination, whether a guide exists BEFORE the user commits to it.
  const [guideList, setGuideList] = useState(null);
  const [guidePickerFor, setGuidePickerFor] = useState(null);
  const [enrichInfoWanted, setEnrichInfoWanted] = useState(true);
  const [enrichHoursWanted, setEnrichHoursWanted] = useState(true);
  // Off by default — a new behavior change, not something every trip wants.
  // When on, reserves a real "Lunch" event (no linked place, same as a
  // manually-typed schedule item) around midday each day instead of
  // scheduling straight through it.
  const [wantLunch, setWantLunch] = useState(false);
  // "AI" place source (2026-10-16): the AI chooses the places for the whole
  // trip — day, time-of-day hint and visit length each — and the schedule is
  // built with Common/aiDaySchedule.js (see Common/aiTripPlaces.js). These
  // are its own options, shown only when AI is the source.
  const [aiMeals, setAiMeals] = useState({ breakfast: false, lunch: false, dinner: false });
  const [aiInclude, setAiInclude] = useState("");
  const [aiIncludeOnly, setAiIncludeOnly] = useState(false);
  const [aiExclude, setAiExclude] = useState("");
  const [aiStatusText, setAiStatusText] = useState("");
  // The run's AI answer: { destinations, promise → { entries } | { error } },
  // asked once at the start; and each found place's plan (day, time hint),
  // keyed by name|city so it survives the review step.
  const aiTripRef = useRef(null);
  const [showDescribe, setShowDescribe] = useState(false);
  // "This guide's places" source (2026-10-16, Common/placesToDestinations.js).
  // suggestedFromPlaces: the destinations shown were suggested from the
  // places (the guide had none) — undone if another source is chosen.
  // thisGuidePick: the review picker's list while it's open; thisGuideByDestRef
  // the run's places per destination; thisGuideCurrentRef the current one's.
  const [suggestedFromPlaces, setSuggestedFromPlacesState] = useState(false);
  const suggestedFromPlacesRef = useRef(false);
  const setSuggestedFromPlaces = (v) => { suggestedFromPlacesRef.current = v; setSuggestedFromPlacesState(v); };
  const [thisGuidePick, setThisGuidePick] = useState(null);
  const thisGuideByDestRef = useRef(null);
  const thisGuideCurrentRef = useRef([]);
  // "This guide's places" into a NEW guide (2026-10-17) — e.g. someone
  // reading a Featured guide who wants their own trip from it. Creating the
  // new guide replaces this one, so its places are copied first:
  // { json (this guide's places/schedule/categories), ids (the ones chosen) }.
  // copyPendingRef: the review picker was opened before the new guide exists.
  const copySnapshotRef = useRef(null);
  const copyPendingRef = useRef(false);
  const aiPlanRef = useRef(new Map());
  // "Add to this guide" only ever touched Places/Schedule, never Home
  // content — by design, so hand-written Home content never gets silently
  // overwritten (see runNextDestination). That also meant the itinerary
  // (and its header image) never reflected the newly-added trip at all when
  // adding to a guide that already had real Home content. Defaults to true:
  // when you're specifically adding to a guide/schedule, you almost always
  // want the itinerary to actually match what's now on the schedule — a
  // user with meaningful hand-written Home content they don't want touched
  // can still opt out. Found 2026-08-31.
  const [updateItinerary, setUpdateItinerary] = useState(true);
  // Defaults to Relaxed (1.3), not Normal (1) — real trip feedback that
  // auto-built schedules felt "too busy" by default. Ported to mobile's
  // BuildMyTripWizard.js/BuildScheduleDialog.js the same day.
  const [paceMultiplier, setPaceMultiplier] = useState("1.3");
  // Minutes subtracted from a transport-derived destination's stated
  // departure time before scheduling stops (see DEPARTURE_PADDING_MINUTES in
  // buildScheduleFromAddedPlaces) — only meaningful for destinations with
  // hasTransportTimes (Destinations From Schedule); default 60, user can set
  // to 0 to disable it entirely.
  const [departurePaddingMinutes, setDeparturePaddingMinutes] = useState("0");
  const advancedRef = useRef(false);
  const enrichAbortRef = useRef(false);
  // Schedule/source/enrich options (shared across every destination in the
  // list) are all collected up front, in the single setup dialog — stashed
  // here and read once each destination's search (STEP_AI_SEARCH/
  // STEP_GOOGLE_SEARCH, whichever the chosen source needs) finishes.
  const scheduleOptionsRef = useRef(null);
  // Destinations still to process, and the one currently being searched/
  // built — runNextDestination() below walks this queue one at a time so
  // each city gets its own search pass and its own date-bounded schedule
  // build, in order, before a single enrichment pass runs at the very end.
  const destinationQueueRef = useRef([]);
  const currentDestRef = useRef(null);
  // Full, unfiltered destinations (including noPlace/"Sea Day" entries) —
  // snapshotted at the very top of startBuild(), before "A new guide"/
  // "Replace this guide" can swap/reset window.guide_obj out from under it.
  // `destinations` state (and validDestinations below) is filtered to
  // real-place entries only (placedDestinationsFromGuide), so reading
  // effectiveDestinations() again later — after a guide swap — would only
  // see the new/cleared guide's empty destinations, silently dropping every
  // sea day the guide actually had. Found 2026-09-04.
  const allDestinationsRef = useRef([]);
  // Bumped every time runNextDestination() advances — STEP_AI_SEARCH and
  // STEP_GOOGLE_SEARCH's effects are keyed on [step, destRunToken] instead
  // of just [step], since two consecutive destinations using the same
  // source would otherwise set the exact same step value and never
  // re-trigger the search effect for the second one.
  const [destRunToken, setDestRunToken] = useState(0);
  // Google source only — how long the source+filter pass took, shown once
  // search finishes as a rough "creation speed" indicator. null for AI.
  const [googleSearchElapsedMs, setGoogleSearchElapsedMs] = useState(null);
  const [googleSearchError, setGoogleSearchError] = useState("");
  const [enrichProgress, setEnrichProgress] = useState("");
  // Snapshotted the moment the wizard opens, then advanced forward after
  // each destination's schedule is built — now that it can run against a
  // guide that already has places/a schedule (adding a second leg/day), each
  // build must only ever touch places added since the LAST checkpoint, never
  // places that were already there before (either from before the wizard
  // opened, or from an earlier destination in this same run).
  const existingPlaceIdsAtStartRef = useRef(new Set());
  // Union of every place ID added across the WHOLE wizard run (unlike
  // existingPlaceIdsAtStartRef, which rolls forward after each destination
  // and only reflects the most recent one). runEnrichment reads this so it
  // only re-enriches places this run actually added, not the guide's entire
  // placesList — a guide with lots of pre-existing places would otherwise
  // re-run paid photo/info/hours lookups over places that have nothing to
  // do with this run (see memory: project_cost_revenue_viability).
  const runAddedPlaceIdsRef = useRef(new Set());
  // Per-destination breakdown of runAddedPlaceIdsRef, keyed by
  // "${city}|${country}" — runEnrichment's cache write (session place search
  // cache, see SessionPlaceSearchCache.js) needs to know which of the run's
  // combined enriched places belong to which destination, so a LATER build
  // for that same destination can reuse the fully-enriched data (not just
  // the raw pre-enrichment search results, which delivered no real speedup
  // — enrichment, not search, is the slow part). Requested 2026-09-27.
  const destinationPlaceIdsRef = useRef({});
  // Whether the CURRENT destination's results are safe to write into that
  // shared cache. Google-sourced results always are (preference-agnostic,
  // pure geography). AI-sourced results (STEP_AI_SEARCH) only are when
  // window.guide_obj.aiPreferences was empty at search time — an AI search
  // run WITH preferences personalizes its results via the LLM prompt, and
  // this cache is keyed by city+country alone, so writing a personalized
  // result here would risk serving one guide's personalized places to a
  // later, differently-configured guide for the same city. Set right before
  // buildCurrentDestinationAndAdvance runs, read when
  // destinationPlaceIdsRef's entry for this destination is recorded.
  // Requested 2026-09-27 (shared-cache extension to AI search).
  const destCacheEligibleRef = useRef(true);
  // Places taken from a guide ("From existing guides") arrive with the
  // guide's own notes. enrichPlaceInfo must never replace those with a
  // Wikipedia summary — tracked by object (addPlace keeps the same object
  // and only changes its ID). 2026-10-12.
  const guideSourcedPlacesRef = useRef(new WeakSet());
  // Trip-wide mosaic group ("Guide") spanning every destination — created
  // once in startBuild() (see proceed() below) when there's more than one
  // destination, so it exists before the first destination's places are
  // added; skipped for a single destination, where it'd just duplicate that
  // destination's own group with the same set of places. Matches TripAI.js's
  // identical group. Requested 2026-09-26.
  const tripWideGroupIdRef = useRef(null);
  // "Replace this guide" clears window.guide_obj.document to a real,
  // non-empty placeholder string (reducer.js's setGuide "clear" action), not
  // "" — the empty-check in runNextDestination below would otherwise treat
  // that placeholder as real Home content and skip generating the itinerary.
  const guideWasClearedRef = useRef(false);
  // Set when runNextDestination builds the itinerary HTML itself (see below)
  // — that happens BEFORE runEnrichment's photo/info/hours enrichment runs,
  // so the freshly-built HTML has each place's photo baked in as empty
  // (enrichPlacePhoto hasn't populated it yet). Tells runEnrichment to
  // rebuild the same itinerary again once enrichment finishes, so the
  // photos placeCard now has actually show up in it too.
  const builtOwnItineraryRef = useRef(false);
  // props.autoStart (DestinationsScreen.js's "Find Places" offer, Places.js/
  // SchedulePanel.js/Map.js's empty-guide hints) — destinations and
  // guide-target are already decided by the time this opens, so skip the
  // setup dialog entirely and go straight into the search. Two variants,
  // per props.autoStartPickPlacesAuto: true (default) = fully automated,
  // straight through to a built schedule/itinerary with no review; false =
  // guided, lands on the Places screen's choose/done pills so the user picks
  // places themselves, still auto-building the schedule/itinerary once
  // they're done. Sensible defaults for everything else the setup dialog
  // would otherwise ask (pace, source, enrichment) — the user can still
  // adjust individual places afterward either way.
  // A real state variable, not a ref — it must land in the SAME batched
  // render as pickPlacesAuto/destinations/guideTarget below, or the trigger
  // effect right after this one (which runs in the same synchronous
  // post-mount flush) reads this flag's fresh value alongside those OTHER
  // fields' still-stale ones, firing startBuild() a render too early with
  // the wrong pickPlacesAuto ("Let Me Pick" silently building fully
  // automated instead). A ref would apply immediately, ahead of the batched
  // state, which is exactly what caused that bug.
  const [autoStartPending, setAutoStartPending] = useState(false);

  useEffect(() => {
    if (props.show) {
      existingPlaceIdsAtStartRef.current = new Set((window.guide_obj?.places?.placesList || []).map((p) => p.ID));
      runAddedPlaceIdsRef.current = new Set();
      // Skipped when initialDestinations is present — those haven't been
      // written onto the guide yet (that's the point, see the destinations
      // state's own comment), so resyncing from placedDestinationsFromGuide
      // here would silently replace them with the guide's OWN (unrelated)
      // destinations the moment this modal opens, undoing the fix before
      // the user ever saw the imported ones. Matches mobile's identical
      // guard. Found 2026-09-28.
      defaultDestRef.current = false;
      // the wizard stays mounted while hidden, so destinations set at mount came from whatever guide was open
      // then (a Trip Builder link opened over the sample guide showed its Florence and Rome, 2026-10-31):
      // given destinations replace them every time it opens
      if (props.initialDestinations) setDestinations(props.initialDestinations);
      if (!props.initialDestinations) {
        if (window.guide_obj && effectiveDestinations().length === 0 && guideDefaultDestinations().length > 0) applyGuideDefaultDestinations();
        else setDestinations(placedDestinationsFromGuide());
      }
      setShowDestinationsScreen(false);
      // Defaults to "Main" — Schedule.js's geScheduleNames() always
      // synthesizes a "Main" entry regardless of whether it has any events
      // yet, so as far as the user is concerned it always exists (the
      // Schedule screen always opens showing it). Defaulting the picker to
      // "Create new schedule" instead meant creating a second schedule
      // happened without the user ever explicitly choosing to — they'd
      // have no reason to know it exists and go looking for it. Found
      // 2026-09-08.
      setScheduleTarget(props.initialScheduleTarget || "Main");
      setNewScheduleName("");
      setGuideTarget(props.initialGuideTarget || "new");
      setNewGuideName("");
      setGuideActionAlert(null);
      // "Build Trip with AI" (props.autoOpenAI) opens with the AI tile chosen.
      setPlaceSource(props.autoOpenAI ? "ai" : "google");
      setAiMeals({ breakfast: false, lunch: false, dinner: false });
      setAiInclude("");
      setAiIncludeOnly(false);
      setAiExclude("");
      setAiStatusText("");
      aiTripRef.current = null;
      setShowDescribe(false);
      setSuggestedFromPlaces(false);
      setThisGuidePick(null);
      thisGuideByDestRef.current = null;
      setPickPlacesAuto(props.autoStart ? (props.autoStartPickPlacesAuto ?? true) : true);
      setFromGuides(!!props.initialFromGuides && !props.autoOpenAI);
      setGuideSources({});
      setGuidePickerFor(null);
      setEnrichInfoWanted(true);
      setEnrichHoursWanted(true);
      // Defaults to Relaxed (1.3), not Normal (1) — real trip feedback that
      // auto-built schedules felt "too busy" by default.
      // props.autoStartPaceMultiplier — MobileBottomNavBar.js's "Find
      // Places" nudge's own Pace choice, since its "Let Me Pick" variant
      // autoStarts straight past this screen's own Pace picker below.
      setPaceMultiplier(String(props.autoStartPaceMultiplier || 1.3));
      setDeparturePaddingMinutes("0");
      setShowAdvanced(false);
      setDestError("");
      setGoogleSearchError("");
      setSearchLimitHit(false);
      setGoogleSearchElapsedMs(null);
      setStep(STEP_SETUP);
      setAutoStartPending(!!props.autoStart);
      // No longer jumps straight to the destinations editor when there are
      // none (2026-10-16): the dialog now starts with other ways in —
      // "Describe your trip", or a source that suggests destinations — so
      // it opens on the dialog itself; the empty Destinations row says
      // "Add where you're going to get started".
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.show]);

  // Opened from "Build Trip from Places" (Places menu) or Build Trip from
  // Image or Text: "This guide's places" chosen, with review — people using
  // that entry came to choose places. Runs after the reset above.
  useEffect(() => {
    if (!props.show) { thisGuideLimitRef.current = null; return; }
    const ids = props.initialThisGuide?.placeIds;
    thisGuideLimitRef.current = Array.isArray(ids) && ids.length > 0 ? new Set(ids.map(String)) : null;
    if (props.initialThisGuide && thisGuideEligible().length > 0) {
      chooseSource("thisguide");
      setPickPlacesAuto(false);
      // Build Trip from Places schedules the places picked in THIS guide,
      // so it keeps "Add to this guide" (adding to Main), unlike choosing
      // the This guide tile, which now defaults to a new guide (2026-10-22).
      setGuideTarget("add");
      setScheduleTarget("Main");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.show]);

  // Fires startBuild() once, on the first render after the reset effect
  // above has actually landed its state (setState is async — calling
  // startBuild() directly in that effect would still see the PREVIOUS
  // render's stale destinations/guideTarget/pickPlacesAuto via closure).
  useEffect(() => {
    if (autoStartPending && step === STEP_SETUP && destinations.length > 0) {
      setAutoStartPending(false);
      startBuild();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStartPending, destinations, guideTarget, pickPlacesAuto, step]);

  useEffect(() => {
    setKey(process.env.REACT_APP_GOOGLE_MAPS_GEOCODING_KEY);
  }, []);

  const isGuestUser = () => !user_info?.uid || user_info.uid === "guest";
  const markGuestUnsaved = () => {
    if (isGuestUser()) window.guest_generated_unsaved_trip = true;
  };

  const closeWizard = () => {
    advancedRef.current = false;
    if (describedSnapshotRef.current) {
      const snap = describedSnapshotRef.current;
      window.guide_obj.destinations = snap.destinations;
      window.guide_obj.aiPreferences = snap.aiPreferences;
      dispatch(slice1Actions.setGuideHasUnsavedChanges({ value: snap.unsaved }));
      describedSnapshotRef.current = null;
    }
    if (defaultDestRef.current || suggestedFromPlacesRef.current) window.guide_obj.destinations = [];
    defaultDestRef.current = false;
    suggestedFromPlacesRef.current = false;
    props.onHide();
  };

  // PromptModal always calls onHide after any button (including its own "×"),
  // whether or not that button had a real Method. Advancing steps sets
  // advancedRef so this guard can tell "we just moved forward" apart from
  // "the user dismissed/skipped" and only close the wizard for the latter.
  const onHideGuarded = () => {
    if (advancedRef.current) {
      advancedRef.current = false;
      return;
    }
    closeWizard();
  };

  // Reopens after editing in DestinationsScreen — re-reads the guide's
  // persisted list (only entries with a real place) so this wizard's own
  // read-only display and build queue stay in sync with whatever was just
  // saved there.
  // ── The guide's own destination as a default (2026-10-17) ──
  // A guide with no destinations but a city in its details (a Featured
  // guide's "Milan, Italy") opens with that city, tomorrow, ready to edit —
  // what the destinations editor used to show when the dialog opened
  // straight into it. Written to the guide so the editor shows it too, and
  // taken back off if the dialog closes without building (defaultDestRef).
  const defaultDestRef = useRef(false);
  // The open guide's destinations/preferences/unsaved flag from before
  // "Describe your trip" replaced them — put back if the wizard closes
  // without building, so describing a trip (e.g. for "A new guide") and then
  // cancelling never leaves the open guide changed (2026-10-24). Cleared once
  // a build starts: "add"/"replace" are meant to change it.
  const describedSnapshotRef = useRef(null);
  const guideDefaultDestinations = () => {
    const attrs = window.guide_obj?.attributes;
    const city = attrs?.cities?.[0], country = attrs?.countries?.[0];
    if (!city || !country) return [];
    const startH = GuideSettings?.schedule_day_start ?? 9;
    const endH = GuideSettings?.schedule_day_end ?? 20;
    const tomorrow = Common.LuxonDateFromJSDate(new Date()).plus({ days: 1 }).startOf("day");
    return [{ id: Date.now(), city, country, dateStart: tomorrow.set({ hour: startH }), dateEnd: tomorrow.set({ hour: endH }) }];
  };
  const applyGuideDefaultDestinations = () => {
    const def = guideDefaultDestinations();
    window.guide_obj.destinations = def.map(cleanDestination);
    defaultDestRef.current = def.length > 0;
    setDestinations(placedDestinationsFromGuide());
  };

  // ── "This guide's places" ──
  // Limited to props.initialThisGuide.placeIds when opened with them (Build
  // Trip from Image or Text's new places).
  const thisGuideLimitRef = useRef(null);
  const thisGuideEligible = () => {
    const all = eligibleGuidePlaces(window.guide_obj?.places?.placesList, window.guide_obj?.categories);
    const limit = thisGuideLimitRef.current;
    return limit ? all.filter((p) => limit.has(String(p.ID))) : all;
  };
  const chooseSource = (key) => {
    const wasSuggested = suggestedFromPlaces;
    if (key === "guides") setFromGuides(true);
    else { setFromGuides(false); setPlaceSource(key === "search" ? "google" : key); }
    if (key === "thisguide") {
      // Defaults to "A new guide, with copies of these places" (2026-10-22),
      // like every other source: "Add to this guide" (which, when chosen,
      // still REPLACES "Main" by default — selectGuideTarget does that) silently rebuilt the open guide with no warning and kept its
      // name, which looked like "A new guide" not working.
      selectGuideTarget("new");
      if (placedDestinationsFromGuide().length === 0 || defaultDestRef.current) {
        const { destinations: suggested } = suggestDestinations(thisGuideEligible(), {
          firstDay: Common.LuxonDateFromJSDate(new Date()).plus({ days: 1 }),
          startH: GuideSettings?.schedule_day_start ?? 9,
          endH: GuideSettings?.schedule_day_end ?? 20,
          pace: Number(paceMultiplier) || 1,
        });
        if (suggested.length > 0) {
          // Written to the guide, so the destinations editor shows them.
          window.guide_obj.destinations = suggested.map(cleanDestination);
          setDestinations(placedDestinationsFromGuide());
          setSuggestedFromPlaces(true);
          defaultDestRef.current = false;
        }
      }
    } else if (wasSuggested) {
      applyGuideDefaultDestinations();
      setSuggestedFromPlaces(false);
    }
    // Other sources add to "Main" by default (adding a city to a trip must
    // not delete it) — undo the This-guide default if it's still in place.
    if (key !== "thisguide" && scheduleTarget === OVERWRITE_PREFIX + "Main") setScheduleTarget("Main");
  };
  // The chosen places of the guide we started from, as new Place objects for
  // the new guide (placesForTarget: categories matched by name, per-trip
  // state cleared). Categories the new guide lacks are added first so the
  // places keep them. Not yet in the guide — the run adds each
  // destination's share.
  const copiedPlacesForNewGuide = (copy) => {
    const g = window.guide_obj;
    const used = new Set();
    copy.json.places.placesList.forEach((p) => {
      if (copy.ids.has(String(p.ID))) (Array.isArray(p.Category) ? p.Category : []).forEach((id) => used.add(String(id)));
    });
    const have = new Set((g.categories || []).map((c) => String(c.name || "").toLowerCase()));
    (copy.json.categories || []).forEach((c) => {
      const name = String(c.name || "");
      if (!used.has(String(c.id)) || !name || have.has(name.toLowerCase())) return;
      const id = g.addCategory(name);
      const added = g.categories.find((x) => x.id === id);
      if (added && c.poi_img != null) added.poi_img = c.poi_img;
      have.add(name.toLowerCase());
    });
    return placesForTarget(copy.json, g).filter((p) => copy.ids.has(String(p.ID)));
  };

  // Which of the guide's places go to which destination, and which fall
  // outside every one (shown under the destinations).
  const thisGuidePlan = () => {
    const valid = destinations.filter((d) => d.country && d.city && !d.noSearch);
    return { valid, ...assignToDestinations(thisGuideEligible(), valid) };
  };

  // "Describe your trip": the parsed destinations and preferences replace
  // the guide's (as Destinations/Preferences' own Save does — a transcript
  // can't be merged field by field), and the source becomes AI, the one that
  // uses the preferences.
  const applyDescribedTrip = (dests, aiPreferences) => {
    if (!describedSnapshotRef.current)
      describedSnapshotRef.current = { destinations: window.guide_obj.destinations, aiPreferences: window.guide_obj.aiPreferences, unsaved: GuideHasUnsavedChanges };
    window.guide_obj.destinations = dests.map(cleanDestination);
    window.guide_obj.aiPreferences = aiPreferences;
    markGuestUnsaved();
    dispatch(slice1Actions.setGuideHasUnsavedChanges({ value: true }));
    setDestinations(placedDestinationsFromGuide());
    setFromGuides(false);
    setPlaceSource("ai");
    setShowDescribe(false);
  };

  const closeDestinationsScreen = () => {
    defaultDestRef.current = false;
    setSuggestedFromPlaces(false);
    setShowDestinationsScreen(false);
    setDestinations(placedDestinationsFromGuide());
  };

  // Auto-shifts every destination's dates by the same offset so the whole
  // (possibly multi-city) sequence starts the day right after the chosen
  // schedule's last existing event (Schedule.getStartEndDate) — appending a
  // "day 2" onto an existing 1-day schedule needs to actually start on day
  // 2, not silently reuse or collide with day 1 — while preserving each
  // destination's own relative position/length. Left alone (today's
  // defaults) when creating a new schedule.
  // "A new guide"/"Replace this guide" both mean whatever schedule ends up
  // holding these destinations is a fresh one either way (a just-created
  // guide has none yet; a cleared guide's schedule is wiped) — so the
  // "Which schedule?" choice below only makes sense, and is only shown, for
  // "Add to this guide". Force it back to the canonical "Main" default
  // whenever the user leaves "add", so a stale pick against the CURRENT
  // guide's schedule names doesn't silently carry over to the new/cleared
  // one — and so switching back to "add" later shows the same default
  // every guide-creation wizard uses, not "create a new schedule".
  const selectGuideTarget = (value) => {
    setGuideTarget(value);
    // "Add to this guide" defaults to "Add to a schedule" (Main), for every
    // source — no longer overwriting Main for This guide (2026-10-22).
    if (value !== "add" || scheduleTarget.startsWith(OVERWRITE_PREFIX)) setScheduleTarget("Main");
  };

  // Auto-shifts every destination's dates by a precise offset (date AND
  // time, not whole days) so the whole sequence starts right after the
  // chosen schedule's last existing event. If that event ends with
  // reasonable time left in the day (before the guide's own day-end hour),
  // the new destination is allowed to start that SAME afternoon instead of
  // always reserving the whole rest of the day; only falls back to the
  // next day (at the guide's day-start hour) when there isn't. Matches
  // mobile's identical fix. Found 2026-09-28: the previous whole-day
  // rounding always skipped to the next full day even when the last event
  // ended at, say, 10am.
  const onScheduleTargetChange = (value) => {
    setScheduleTarget(value);
    // Overwriting starts the schedule over, so the dates stay as they are.
    if (value === NEW_SCHEDULE_VALUE || value.startsWith(OVERWRITE_PREFIX)) return;
    const startH = GuideSettings?.schedule_day_start ?? 9;
    const endH = GuideSettings?.schedule_day_end ?? 20;
    const { endDate: existingEnd } = window.guide_obj.schedule.getStartEndDate(value);
    const hasRoomLeftToday = existingEnd.hour < endH;
    const newStart = hasRoomLeftToday
      ? existingEnd
      : existingEnd.plus({ days: 1 }).startOf("day").set({ hour: startH });
    setDestinations((prev) => {
      if (prev.length === 0) return prev;
      const delta = newStart.diff(prev[0].dateStart);
      if (Math.abs(delta.as("minutes")) < 1) return prev;
      return prev.map((d) => ({ ...d, dateStart: d.dateStart.plus(delta), dateEnd: d.dateEnd.plus(delta) }));
    });
  };

  // "From existing guides": load the guides the user could take places from
  // (their own + Featured) the first time the option is chosen, so each
  // destination can show whether a guide exists before anything is built.
  useEffect(() => {
    if (!fromGuides || guideList !== null) return;
    let cancelled = false;
    const load = (type) => new Promise((resolve) => DataBase.getAllGuideInfo(user_info.uid, (res) => resolve(res?.pass ? res.res || [] : []), type));
    Promise.all([load("Mine"), load("Featured")]).then(([mine, featured]) => {
      if (cancelled) return;
      const seen = new Set();
      const all = [...mine, ...featured].filter((g) => {
        if (!g || !g.data || seen.has(g.id)) return false;
        seen.add(g.id);
        // Not the guide being built into, and only guides that allow copying.
        return g.data.guide_id !== window.guide_obj?.guide_id && canCopyFrom(g.data, user_info.uid);
      });
      setGuideList(all);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromGuides]);
  // Guides whose own city (and country, when the guide records one) is this
  // destination — the guide-level city, not each place's, which is unreliable.
  const guidesForDestination = (d) => {
    if (!guideList) return [];
    const want = cityKey(d.city, "");
    const wantCountry = cityKey("", d.country);
    return guideList.filter((g) => {
      const cities = Array.isArray(g.data.cities) ? g.data.cities : [];
      const countries = Array.isArray(g.data.countries) ? g.data.countries : [];
      return cities.some((c) => cityKey(c, "") === want) && (countries.length === 0 || countries.some((c) => cityKey("", c) === wantCountry));
    });
  };
  // { country: [cities] } of every guide that can be built from — what the
  // Destinations screen offers in this mode. Only single-country guides: a
  // multi-country guide's cities can't be paired with its countries. Country
  // spellings that differ only by punctuation ("Czech_Republic") are shown
  // once, tidied.
  const guideCitiesByCountry = () => {
    const byKey = new Map(); // country key -> { label, cities: Map(city key -> label) }
    (guideList || []).forEach((g) => {
      const countries = Array.isArray(g.data.countries) ? g.data.countries.filter(Boolean) : [];
      const cities = Array.isArray(g.data.cities) ? g.data.cities.filter(Boolean) : [];
      if (countries.length !== 1 || cities.length === 0) return;
      const label = String(countries[0]).replace(/_/g, " ").trim();
      const key = cityKey("", label);
      if (!byKey.has(key)) byKey.set(key, { label, cities: new Map() });
      const entry = byKey.get(key);
      cities.forEach((c) => { const ck = cityKey(c, ""); if (!entry.cities.has(ck)) entry.cities.set(ck, String(c).trim()); });
    });
    const out = {};
    byKey.forEach((v) => { out[v.label] = [...v.cities.values()]; });
    return out;
  };
  // One matching guide → fill the slot in, so the common case needs no taps.
  useEffect(() => {
    if (!fromGuides || !guideList) return;
    setGuideSources((prev) => {
      const next = { ...prev };
      let changed = false;
      destinations.filter((d) => d.city && d.country && !d.noSearch).forEach((d) => {
        const key = cityKey(d.city, d.country);
        if (next[key]) return;
        const matches = guidesForDestination(d);
        if (matches.length === 1) { next[key] = [{ guide_info_id: matches[0].id, guide_name: matches[0].data.guide_name }]; changed = true; }
      });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromGuides, guideList, destinations]);
  const onGuidePicked = (guide_info_id, guide_id, guide_name) => {
    const d = guidePickerFor;
    setGuidePickerFor(null);
    if (!d || !guide_info_id) return;
    const key = cityKey(d.city, d.country);
    setGuideSources((prev) => {
      const have = prev[key] || [];
      if (have.some((g) => g.guide_info_id === guide_info_id)) return prev;
      return { ...prev, [key]: [...have, { guide_info_id, guide_name }] };
    });
  };

  // Walks destinationQueueRef one destination at a time — each gets its own
  // search pass (STEP_AI_SEARCH/STEP_GOOGLE_SEARCH) and its own date-bounded
  // schedule build (see buildCurrentDestinationAndAdvance below) before the
  // next one starts. Once the queue is empty, navigates to the Schedule
  // screen and runs a single enrichment pass over everything added.
  const runNextDestination = () => {
    const next = destinationQueueRef.current.shift();
    if (!next) {
      // Auto-fix pass — run once, over the whole finished schedule, before
      // the itinerary HTML below is generated from it. Push-pull (small
      // same-day shift/shrink) runs first since it's less disruptive; swap
      // (closed-all-day or a closes_during too big for push-pull) catches
      // whatever's left. Requested 2026-09-03; push-pull + closes_during
      // added 2026-09-14.
      const finishedEvents = window.guide_obj.schedule.getEventsFromSchedule(scheduleOptionsRef.current?.scheduleName);
      Schedule.autoResolvePushPullConflicts(finishedEvents, window.guide_obj.places);
      Schedule.autoResolveClosedAllDayConflicts(finishedEvents, window.guide_obj.places);

      // A brand-new (or replaced) guide has no Home content of its own yet —
      // without this it stays permanently blank, since nothing else in this
      // wizard's flow ever calls createHTMLItinerary (unlike the legacy
      // TripAI.js flow this replaced, which built it into Home whenever the
      // trip landed on the Main schedule). Only fills it in when it's
      // genuinely empty — appending to a guide that already has its own Home
      // content shouldn't silently overwrite it.
      const doc = window.guide_obj.document;
      // "Replace this guide" leaves a real, non-empty placeholder string in
      // document (reducer.js's setGuide "clear" action) — guideWasClearedRef
      // catches that case too, since it's just as much "no real itinerary
      // yet" as a genuinely empty/never-set document. props.fromDestinationsNudge
      // (the "Find Places" nudge — DestinationsScreen.js's post-save offer,
      // Places.js/SchedulePanel.js/Map.js's empty-guide hints, BOTH the
      // "Auto-Build" and "Let Me Pick" variants) also forces a build: those
      // all fire specifically because the guide had zero places yet, so
      // whatever unrelated Home content it happened to have isn't a real
      // itinerary worth protecting the way the check below assumes for the
      // NORMAL "add to this guide" flow (where a guide might have real
      // hand-written Home content despite having no places yet, and
      // shouldn't be silently overwritten). Deliberately NOT props.autoStart
      // here — that only reflects "skip the setup dialog," which "Auto-Build"
      // doesn't set, so using it left Auto-Build silently building places
      // with no itinerary. guideTarget==="add" && updateItinerary is the
      // explicit opt-in for that NORMAL "add to this guide" case — the user
      // asked for the itinerary to reflect the schedule they just added to,
      // an explicit choice (default on) rather than the doc-emptiness guard
      // this whole check otherwise relies on.
      if (!doc || doc === "<!-- Comment -->" || isPlaceholderDoc(doc) || guideWasClearedRef.current || props.fromDestinationsNudge || (guideTarget === "add" && updateItinerary)) {
        builtOwnItineraryRef.current = true;
        // eventBody: false — automated creation shouldn't prompt for this
        // choice the way ScheduleHeader.js's manual ItinSettings dialog
        // does; showing only place.Info avoids the AI-generated
        // event.Body duplicating/overlapping it. See memory:
        // project_cost_revenue_viability.
        const html_itin_String = window.guide_obj.createHTMLItinerary(scheduleOptionsRef.current?.scheduleName, { info: true, photo: true, directions: true, place_menu: true, eventBody: false });
        window.guide_obj.document = `<p><h3>${window.guide_obj.attributes.guide_name}</h3></p></br>${html_itin_String}`;
        // The Home/Itinerary screen actually renders Redux's state.homeScreen,
        // not window.guide_obj.document directly. resetHomeScreen re-reads
        // window.guide_obj.document (falling back to createGuideHTML only
        // when it's still empty) and writes it into state.homeScreen; the
        // follow-up setGuide({guide:null, fitToBounds:true}) matches
        // HomeHeader.js's own updateUIAndHome exactly — resetHomeScreen alone
        // left other guide-derived UI (map bounds, place lists) stale in
        // testing.
        // resetHomeScreen now regenerates Photo Mosaic content using the
        // CURRENT state.ScheduleName (2026-09-27 fix, for ScheduleHeader.js's
        // "Choose Schedule") instead of hardcoded "Main" — but this run only
        // ever dispatches setScheduleName on the AI-search path (see
        // SetScheduleName below), never the Google-search path, so
        // state.ScheduleName could be stale from whatever the user was
        // looking at before opening this wizard. Sync it here so
        // resetHomeScreen always regenerates the schedule this run actually
        // just built, regardless of search source.
        dispatch(slice1Actions.setScheduleName({ value: scheduleOptionsRef.current?.scheduleName }));
        dispatch(slice1Actions.resetHomeScreen({ value: null }));
        dispatch(slice1Actions.setGuide({ guide: null, fitToBounds: true }));
      }
      // Flags this as a guide with real content that now needs saving —
      // covers "Add to this guide"/"Replace this guide" (an already-saved
      // guide, so guide_info_id alone never catches it) alongside "A new
      // guide" (which guide_info_id already catches, but setting this too
      // is harmless/uniform). Port of andiamo-places' identical fix.
      // Added 2026-09-30.
      dispatch(slice1Actions.setGuideHasUnsavedChanges({ value: true }));
      goToItinerary();
      runEnrichment(scheduleOptionsRef.current?.enrichInfoWanted, scheduleOptionsRef.current?.enrichHoursWanted);
      return;
    }
    currentDestRef.current = next;
    advancedRef.current = true;
    setDestRunToken((t) => t + 1);
    // The AI source runs in the search step too (its own branch there) —
    // STEP_AI_SEARCH (the older per-destination AISearch) is no longer used.
    setStep(STEP_GOOGLE_SEARCH);
  };

  // "Create" — applies every destination to the guide's attributes, resolves
  // the final schedule name once (so every destination's build appends under
  // the exact same name instead of each one computing its own dedup), stashes
  // every collected option (nothing's been searched or added to the guide
  // yet), and kicks off the destination queue. The actual schedule build/
  // enrichment happens as each destination's search resolves (see
  // runNextDestination/buildCurrentDestinationAndAdvance).
  const startBuild = (copyChosenIds = null) => {
    // props.initialDestinations (ImportItineraryImageModal.js) — this is
    // the actual commit point, the first place it's safe to touch the
    // guide at all (see `destinations` state's own comment above). "new"/
    // "replace" discard the current guide's own destinations anyway, so use
    // ONLY what was just imported; "add" must APPEND onto the existing
    // guide's real destinations (read fresh via effectiveDestinations(),
    // since this runs before any guide swap/clear), or they'd be silently
    // replaced by the imported batch even though the user chose to keep
    // them. Matches mobile's identical fix. Found 2026-09-28.
    allDestinationsRef.current = props.initialDestinations
      ? (guideTarget === "add" && !props.replaceDestinations ? [...effectiveDestinations(), ...props.initialDestinations] : props.initialDestinations)
      : effectiveDestinations();
    // !d.built (guideTarget==="add" only) — see cleanDestination's comment:
    // skip destinations an earlier run of this wizard already searched/
    // scheduled, so appending new destinations onto an already-built guide
    // (Import Itinerary, normal Add Destination) doesn't silently re-process
    // the whole trip. destinations state is always seeded from the CURRENT
    // guide (placedDestinationsFromGuide/effectiveDestinations) regardless
    // of guideTarget, so a "new"/"replace" run would otherwise inherit stale
    // built flags left over from the guide being read as a starting
    // template — nothing has actually been built into that new/replaced
    // guide yet, so built must not gate it. Found 2026-09-27.
    // A destination built in an earlier run is skipped when adding to this
    // guide (so nothing is built twice) — except when the trip is rebuilt on
    // purpose: from this guide's own places, or overwriting the schedule.
    // Without this, a guide whose destinations were built before couldn't
    // use "This guide's places" at all (2026-10-17).
    // Overwriting a schedule always rebuilds; otherwise the "Skip
    // destinations already in this trip" box decides. "Already in" now means
    // still in the chosen schedule, not just built once (isInTrip).
    const validDestinations = destinationsToBuild();
    if (validDestinations.length === 0) {
      // Every destination on this guide already has `built: true` from an
      // earlier run — canCreate only checks country/city/dates, not `built`,
      // so the button stays enabled/clickable even though there's nothing
      // left for this filter to build, and this used to just silently
      // return here with zero feedback. Found 2026-09-26 — reported as
      // "the Create button does nothing."
      // destinations (this component's own state) is already filtered by
      // placedDestinationsFromGuide, which silently drops any destination
      // whose dateStart equals dateEnd (zero duration) — so a destination
      // the user just entered can vanish from `destinations` entirely
      // without ever showing up as "already built". Check the guide's raw,
      // unfiltered list to give a real explanation instead of the generic
      // "add a destination" message in that case. Found 2026-09-27 —
      // reproduced by setting a new destination's start/end to the same
      // date and time.
      const hasZeroDuration = allDestinationsRef.current.some((d) => d.city && d.dateStart && d.dateEnd && d.dateStart.equals(d.dateEnd));
      setDestValidationError(
        destinations.length === 0
          ? (hasZeroDuration
              ? "One of your destinations has the same start and end date/time — give it a real date range so it can be built."
              : "Add at least one destination first.")
          : "All of this trip's destinations are already in it (✓). Tap a ✓ to redo that destination, add a new destination, or use Recalculate Schedule to change the existing schedule."
      );
      return;
    }

    // Manually-edited destinations have no equivalent of ScheduleDestinationsFlow.js/
    // PasteDestinationsFlow.js's own overlap/21-day checks — this was mainly
    // built for TripAI.js, but every destination-collecting path here needs
    // the same safeguards so a user hand-editing dates can't create an
    // overlapping or absurdly long trip undetected.
    const overlap = overlapError(validDestinations);
    if (overlap) {
      setDestValidationError(overlap);
      return;
    }
    const lengthError = tripLengthError(validDestinations);
    if (lengthError) {
      setDestValidationError(lengthError);
      return;
    }

    // This guide's places into a new guide: review (if wanted) and copy them
    // now, while this guide is still the open one.
    copySnapshotRef.current = null;
    if (!fromGuides && placeSource === "thisguide" && guideTarget === "new") {
      if (!pickPlacesAuto && !copyChosenIds) {
        const { byDest } = assignToDestinations(thisGuideEligible(), validDestinations);
        copyPendingRef.current = true;
        setThisGuidePick(validDestinations
          .map((d, i) => ({ title: d.city, places: byDest.get(i) || [] }))
          .filter((g) => g.places.length > 0));
        return;
      }
      const g = window.guide_obj;
      copySnapshotRef.current = {
        json: JSON.parse(JSON.stringify({ places: { placesList: g.places.placesList }, schedule: { events: g.schedule.events || [] }, categories: g.categories || [] })),
        ids: new Set((copyChosenIds || thisGuideEligible().map((p) => p.ID)).map(String)),
      };
    }

    const defaultName = validDestinations.length === 1
      ? `${validDestinations[0].city}, ${validDestinations[0].country} Trip`
      : `${validDestinations[0].country} Trip`;

    // The actual build (attribute merge, schedule-name resolution, and
    // kicking off the destination queue) — deferred behind guideTarget's
    // new/replace confirmation below so a brand-new or cleared guide is
    // already in place (window.guide_obj) by the time this runs, exactly as
    // TripAI.js's AddToGuide only ever runs after its own equivalent
    // confirmation/creation (see ConfirmCreate/_createNewGuideAndProceed/
    // _clearGuideAndProceed there).
    const proceed = () => {
      describedSnapshotRef.current = null; // building now — keep what Describe set
      // "A new guide" creates a genuinely fresh Guide instance (its own
      // blank destinations: []) and "Replace this guide" may too, depending
      // on clear_guide's implementation — either way, whatever destinations
      // this wizard is about to build from must be written onto whichever
      // guide is now actually current, or they'd otherwise only ever have
      // existed in this component's own React state, never persisted onto
      // Guide.destinations at all. A no-op duplicate write for "add to this
      // guide" (already the same data, same guide object).
      window.guide_obj.destinations = allDestinationsRef.current.map(cleanDestination);

      // Any filter left active from browsing Places earlier in the session
      // (category, group, visited, a leftover text search, etc.) can hide the
      // places this wizard is about to find/add, even though they're correctly
      // in the guide — they just don't show up until the user happens to clear
      // or notice the filter themselves.
      dispatch(slice1Actions.ClearAllFilters());

      let guide_attributes = JSON.parse(JSON.stringify(window.guide_obj.attributes));
      validDestinations.forEach((d) => {
        if (!guide_attributes.cities.includes(d.city)) guide_attributes.cities.push(d.city);
        if (!guide_attributes.countries.includes(d.country)) guide_attributes.countries.push(d.country);
      });
      // A guide that's still genuinely unnamed gets named regardless of
      // guideTarget — including "add to this guide" (e.g. the Welcome
      // Screen's "start with a blank guide" + Destinations + "let me pick",
      // which always targets "add" since the guide already exists locally).
      // Never overwrites a guide that already has a real name — matches
      // TripAI.js's identical rule. Found 2026-08-27: this wizard never
      // touched guide_name/description at all, for any guideTarget, so a
      // guide built this way was permanently stuck as "New Guide".
      if (guide_attributes.guide_name === "New Guide") {
        guide_attributes.guide_name = defaultName;
        guide_attributes.description = `Trip to ${validDestinations[0].city}, ${validDestinations[0].country}`;
      }
      window.guide_obj.attributes.modifyGuideAttributes(guide_attributes);
      // guide_meta (any value) re-reads the guide's name/details into the
      // name bar, which otherwise showed "Untitled Guide" for a new guide
      // until it was saved — createLocalGuide starts it with guide_info:
      // null. Touches nothing else. 2026-10-16.
      dispatch(slice1Actions.setGuide({ guide: null, guide_meta: {} }));
      markGuestUnsaved();

      // Trip-wide mosaic group ("Guide") spanning every destination — must
      // exist before the first destination's places are added
      // (addMosaicGroupForDestination below tags each newly-added place with
      // it), and after any "new"/"replace" guide swap above so it lands on
      // whichever guide object is now actually current. Matches TripAI.js's
      // identical group/fix. Requested 2026-09-26.
      tripWideGroupIdRef.current = null;
      // Whether this is a "multi-destination trip" must be judged against
      // the GUIDE's total real destinations, not validDestinations (this
      // run's own queue, already excluding whatever's built) — otherwise
      // appending a single new destination onto an already-built
      // multi-destination guide (e.g. adding one more city to a trip) always
      // looked like a "single-destination trip" and silently skipped adding
      // the new destination's places to the guide's existing trip-wide
      // group. Found 2026-09-27 — reported as a newly-added destination
      // missing from the "Guide" mosaic tile.
      const totalRealDestinations = allDestinationsRef.current.filter((d) => d.city && d.country).length;
      if (totalRealDestinations > 1) {
        try {
          const groupName = "Guide";
          const existingGuideGroup = window.guide_obj.findGroupFromName(groupName);
          let gid;
          // Reuse the guide's existing trip-wide group instead of minting a
          // "Guide-2" — same reasoning as addMosaicGroupForDestination's
          // identical reuse-vs-collision fix just above.
          if (existingGuideGroup !== null && existingGuideGroup.mosaic) {
            gid = existingGuideGroup.id;
          } else {
            gid = window.guide_obj.addGroup(groupName);
          }
          const g = window.guide_obj.groups.find((x) => x.id === gid);
          // .schedule must be the REAL schedule this run is building into
          // (matches addMosaicGroupForDestination's identical fix,
          // 2026-09-26) — not the group's own name "Guide", which no
          // schedule is ever actually named, or the mosaic tile's click
          // handler (which filters by group AND schedule) would show zero
          // places.
          if (g) { g.schedule = scheduleOptionsRef.current?.scheduleName || groupName; g.mosaic = true; g.poi_img = 16; }
          tripWideGroupIdRef.current = gid;
        } catch (e) {
          console.log("proceed: whole-trip group creation failed, continuing without it", e.message);
          tripWideGroupIdRef.current = null;
        }
      }

      buildForCurrentGuide();
    };

    // "A new guide" replaces the old two-step flow (create a blank guide
    // separately, then come back here) with one continuous action — create
    // the guide first, then proceed exactly as if the user had started from
    // an existing (now brand-new) guide. Creating it discards whatever is in
    // the currently-open guide locally, so confirm BEFORE creating.
    if (guideTarget === "new") {
      if (!props.create_new_guide && !(isGuestUser() && props.create_local_guide)) {
        setGuideActionAlert({ title: "Error", message: "Creating a new guide isn't available here.", buttons: [{ Title: "OK", Method: null }] });
        return;
      }
      setGuideActionAlert({
        title: "New Guide",
        message: isGuestUser()
          ? "This will create a new guide, replacing what's currently open. You're not signed in, so it'll be kept on this device — sign in, then use Save to add it to your account. Continue?"
          : "This will create a new guide. Save any wanted changes to this guide first. Continue?",
        buttons: [
          {
            Title: "Continue",
            Method: () => {
              const createFn = isGuestUser() && props.create_local_guide ? props.create_local_guide : props.create_new_guide;
              createFn(newGuideName.trim() || defaultName, (success) => {
                if (!success) {
                  setGuideActionAlert({ title: "Error", message: "Could not create the new guide. Please try again.", buttons: [{ Title: "OK", Method: null }] });
                  return;
                }
                proceed();
              });
            },
          },
          { Title: "Cancel", Method: null },
        ],
      });
      return;
    }

    // "Replace this guide" — same reasoning as "A new guide" above: it
    // discards the current guide's places/schedule locally, so confirm
    // BEFORE clearing.
    if (guideTarget === "replace") {
      if (!props.clear_guide) {
        setGuideActionAlert({ title: "Error", message: "Replacing the current guide isn't available here.", buttons: [{ Title: "OK", Method: null }] });
        return;
      }
      const replace = () => { guideWasClearedRef.current = true; props.clear_guide(); proceed(); };
      const baseMessage = isGuestUser()
        ? "This will clear all places and the schedule in the current guide. You're not signed in, so it'll be kept on this device — sign in, then use Save to add it to your account. Continue?"
        : "This will clear all places and the schedule in the current guide. Save any wanted changes to this guide first. Continue?";
      // The owner can also delete the guide's comments, polls and ratings —
      // about the old trip (2026-10-17, same choice as Clear Local Guide
      // Content). Only when there are some.
      discussionToOffer().then((discussion) => {
        setGuideActionAlert({
          title: "Replace Guide",
          message: discussion
            ? `${baseMessage}\n\nThis guide also has ${discussion}. You can delete them too — that part happens right away and can't be undone.`
            : baseMessage,
          buttons: discussion
            ? [
                { Title: "Replace", Method: replace },
                { Title: "Replace and delete discussion", Method: () => { DataBase.deleteGuideDiscussion(guide_meta.guide_info_id); replace(); } },
                { Title: "Cancel", Method: null },
              ]
            : [
                { Title: "Continue", Method: replace },
                { Title: "Cancel", Method: null },
              ],
        });
      });
      return;
    }

    proceed();
  };

  // "N comments, N polls and N ratings" when the signed-in user owns this
  // saved guide and it has any, else null (see "Replace this guide").
  const discussionToOffer = async () => {
    const infoId = guide_meta?.guide_info_id;
    if (!infoId || isGuestUser() || guide_meta?.guide_info?.user_id !== user_info?.uid) return null;
    const c = await DataBase.countGuideDiscussion(infoId);
    const parts = [
      c.comments ? `${c.comments} comment${c.comments === 1 ? "" : "s"}` : "",
      c.polls ? `${c.polls} poll${c.polls === 1 ? "" : "s"}` : "",
      c.reviews ? `${c.reviews} rating${c.reviews === 1 ? "" : "s"}` : "",
    ].filter(Boolean);
    if (parts.length === 0) return null;
    return parts.length > 1 ? `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}` : parts[0];
  };

  // One event per calendar day covered by each noPlace destination (not one
  // multi-day event) — createHTMLItinerary groups by a single day per event,
  // so a multi-day span would only surface on its first day, leaving the
  // rest still gapped. Subject is the destination's free-text label (stored
  // in `country` for a noPlace entry — see DestinationsScreen.js's
  // withNoPlace), falling back to a generic label if somehow blank. Guards
  // against duplicates so re-running Build My Trip onto the same
  // guide/schedule doesn't pile up repeats.
  const addNoPlacePlaceholderEvents = (allDests, scheduleName) => {
    const startH = GuideSettings?.schedule_day_start ?? 9;
    allDests
      .filter((d) => !d.city)
      .forEach((d) => {
        const label = d.country || "No Place Day";
        let cursor = d.dateStart.startOf("day");
        const endDay = d.dateEnd.startOf("day");
        while (cursor <= endDay) {
          const start = cursor.set({ hour: startH, minute: 0, second: 0, millisecond: 0 });
          const isDuplicate = window.guide_obj.schedule.events.some(
            (ex) =>
              ex.Subject === label &&
              ex.Start?.toISODate?.() === start.toISODate() &&
              (ex.Itinerary || "Main") === scheduleName
          );
          if (!isDuplicate) {
            window.guide_obj.schedule.addEvent({
              Subject: label,
              Start: start,
              End: start.plus({ minutes: 30 }),
              Places: "",
              Itinerary: scheduleName,
            });
          }
          cursor = cursor.plus({ days: 1 });
        }
      });
  };

  // Waypoint destinations (noSearch: true, set via DestinationsScreen.js's
  // "Skip place search" toggle) are real, geocoded places kept on the
  // map/schedule — a fuel stop, an overnight stay not worth researching
  // attractions for — but excluded from Build My Trip's attraction search.
  // Creates one Place per waypoint (deduped by Name/City/Country so a
  // re-run doesn't pile up repeats) plus one placeholder event per calendar
  // day, same reasoning as addNoPlacePlaceholderEvents below: a day with
  // zero events isn't a blank "Day N" to createHTMLItinerary, it's not
  // there at all, and every later day silently renumbers.
  const addWaypointPlacesAndEvents = async (allDests, scheduleName) => {
    const startH = GuideSettings?.schedule_day_start ?? 9;
    const waypointDests = allDests.filter((d) => d.city && d.noSearch);
    for (const d of waypointDests) {
      // Label (e.g. "Hotel", "Fuel Stop") names WHAT's there, distinct from
      // the city it's in — falls back to the city name alone when left
      // blank. Included in the dedup match too, so two differently-labeled
      // waypoints in the same city (two different hotels, say) don't
      // collapse into one place.
      const waypointLabel = d.waypointLabel?.trim();
      const label = waypointLabel || d.city;
      let place = (window.guide_obj.places.placesList || []).find((p) => p.Name === label && p.Country === d.country);
      if (!place) {
        let loc = null;
        if (waypointLabel) {
          // A NAMED waypoint (e.g. a specific hotel) — geocode the name
          // directly for a precise pin, skipping the city-center shortcut
          // below entirely (a city-center coordinate would put a hotel in
          // the wrong part of town). Same pattern as
          // ImportConfirmationModal.js's identical hotel-geocoding query.
          try {
            const geoResp = await geocode(RequestType.ADDRESS, `${waypointLabel}, ${d.city}, ${d.country}`);
            loc = geoResp?.results?.[0]?.geometry?.location;
          } catch (e) { /* leave loc null — place still gets created without coords */ }
        } else {
          const cityMatch = (citiesByCountry[d.country] || []).find((c) => c.city.toLowerCase() === d.city.toLowerCase());
          if (cityMatch) {
            loc = { lat: cityMatch.lat, lng: cityMatch.lon };
          } else {
            try {
              const geoResp = await geocode(RequestType.ADDRESS, `${d.city}, ${d.country}`);
              loc = geoResp?.results?.[0]?.geometry?.location;
            } catch (e) { /* leave loc null — place still gets created without coords */ }
          }
        }
        place = new Place(window.guide_obj.places);
        place.Name = label;
        place.City = d.city;
        place.Country = d.country;
        place.Lat = loc?.lat ?? "";
        place.Longi = loc?.lng ?? "";
        place.Source = Place_Source.USER;
        window.guide_obj.places.addPlace(place);
      }
      let cursor = d.dateStart.startOf("day");
      const endDay = d.dateEnd.startOf("day");
      while (cursor <= endDay) {
        const start = cursor.set({ hour: startH, minute: 0, second: 0, millisecond: 0 });
        const isDuplicate = window.guide_obj.schedule.events.some(
          (ex) => ex.Subject === label && ex.Start?.toISODate?.() === start.toISODate() && (ex.Itinerary || "Main") === scheduleName
        );
        if (!isDuplicate) {
          window.guide_obj.schedule.addEvent({
            Subject: label,
            Start: start,
            End: start.plus({ minutes: 30 }),
            Places: place.ID,
            Itinerary: scheduleName,
          });
        }
        cursor = cursor.plus({ days: 1 });
      }
    }
  };

  // Resolves the final schedule name once (so every destination's build
  // appends under the exact same name instead of each one computing its own
  // dedup), stashes every collected option (nothing's been searched or added
  // to the guide yet), and kicks off the destination queue. The actual
  // schedule build/enrichment happens as each destination's search resolves
  // (see runNextDestination/buildCurrentDestinationAndAdvance).
  const buildForCurrentGuide = async (thisGuideChosen = null) => {
    // Building with them makes a default or suggested destination the
    // user's own — closing afterwards keeps it.
    defaultDestRef.current = false;
    suggestedFromPlacesRef.current = false;
    // Same list startBuild checked — this used to rebuild every destination,
    // so "Skip destinations already in this trip" never actually skipped.
    const validDestinations = destinationsToBuild().filter((d) => !d.noSearch);
    thisGuideByDestRef.current = null;
    if (!fromGuides && placeSource === "thisguide") {
      const copy = copySnapshotRef.current;
      const byDest = shareRepeatVisits(assignToDestinations(copy ? copiedPlacesForNewGuide(copy) : thisGuideEligible(), validDestinations).byDest, validDestinations);
      if (!copy && !pickPlacesAuto && !thisGuideChosen) {
        // Review: the guide's places in a picker, all ticked; Build continues
        // from onThisGuidePicked.
        // Grouped by destination for PlacesReviewModal.
        setThisGuidePick(validDestinations
          .map((d, i) => ({ title: d.city, places: byDest.get(i) || [] }))
          .filter((g) => g.places.length > 0));
        return;
      }
      const keep = thisGuideChosen ? new Set(thisGuideChosen) : null;
      thisGuideByDestRef.current = new Map([...byDest.entries()].map(([i, list]) => [i, keep ? list.filter((p) => keep.has(p.ID)) : list]));
    }
    const defaultName = validDestinations.length === 1
      ? `${validDestinations[0].city}, ${validDestinations[0].country} Trip`
      : `${validDestinations[0].country} Trip`;
    // "A new guide"/"Replace this guide" always goes into "Main" — the
    // scheduleTarget picker (and its NEW_SCHEDULE_VALUE default) only
    // applies to "add to this guide", where it's actually shown. Without
    // this, a brand-new/replaced guide's schedule silently got an
    // auto-generated name (e.g. "Florence, Italy Trip") instead of "Main",
    // since scheduleTarget stays at its default when that picker is hidden.
    // Fixed 2026-08-30.
    const overwriting = guideTarget === "add" && scheduleTarget.startsWith(OVERWRITE_PREFIX);
    let finalScheduleName = overwriting ? scheduleTarget.slice(OVERWRITE_PREFIX.length) : scheduleTarget;
    if (guideTarget !== "add") {
      finalScheduleName = "Main";
    } else if (scheduleTarget === NEW_SCHEDULE_VALUE) {
      // Reaching here means the user explicitly picked "Create new
      // schedule" over the default "Main" (see scheduleTarget's own reset
      // above) — geScheduleNames() always includes a "Main" entry
      // regardless of whether it has any events yet, so this is a genuine,
      // deliberate second schedule and should get its own distinct name,
      // not "Main" too.
      const baseSchedule = newScheduleName.trim() || defaultName;
      const scheds = window.guide_obj.schedule.geScheduleNames();
      finalScheduleName = baseSchedule;
      for (let s = 0; s < 99999; s++) {
        finalScheduleName = `${baseSchedule}${s === 0 ? "" : " " + s}`;
        if (scheds.findIndex((x) => x.label === finalScheduleName) === -1) break;
      }
    }
    // Only refusals from THIS run count toward its limit notice.
    // The user chose to start this schedule over: its events go before
    // anything is built (and before "already in this schedule" is checked).
    if (overwriting) window.guide_obj.schedule.deleteAllEvents(finalScheduleName);
    // Destinations being redone, or changed since they were built: the
    // events they replace go first.
    else {
      const redoSchedule = finalScheduleName;
      validDestinations.forEach((d) => removeFromSchedule(d, window.guide_obj, redoSchedule));
    }
    clearDailyLimitNotice();
    scheduleOptionsRef.current = {
      scheduleName: finalScheduleName,
      paceMultiplier: Number(paceMultiplier) || 1,
      wantLunch,
      // A destination with no guide picked (Other guides) falls back to the
      // plain search.
      // Review applies to every source (2026-10-17) — Other guides' places
      // too, in the usual review step. "This guide's places" reviews in its
      // own picker before the run, so the per-destination step auto-adds.
      pickPlacesAuto: !fromGuides && placeSource === "thisguide" ? true : pickPlacesAuto,
      enrichInfoWanted,
      enrichHoursWanted,
      placeSource: fromGuides ? "google" : placeSource,
      validDestinations,
      guideSources: fromGuides ? guideSources : null,
      departurePaddingMinutes: Number(departurePaddingMinutes) || 0,
      aiMeals,
    };
    aiTripRef.current = null;
    aiPlanRef.current = new Map();
    if (!fromGuides && placeSource === "ai") {
      // One question for the whole trip, asked now so it's answered while
      // the waypoints are added; each destination's search step waits on it.
      const days = Math.max(1, Math.round(validDestinations[validDestinations.length - 1].dateEnd.startOf("day").diff(validDestinations[0].dateStart.startOf("day"), "days").days) + 1);
      setAiStatusText(`Choosing places for your trip… this usually takes ${days <= 4 ? "10–20" : days <= 14 ? "20–30" : "30–60"} seconds.`);
      aiTripRef.current = {
        destinations: validDestinations,
        promise: askForPlaces(buildPlacesQuestion({
          destinations: validDestinations,
          prefs: window.guide_obj?.aiPreferences || {},
          startH: GuideSettings?.schedule_day_start ?? 9,
          endH: GuideSettings?.schedule_day_end ?? 20,
          meals: aiMeals,
          include: aiInclude.trim(),
          includeOnly: aiIncludeOnly && aiInclude.trim() !== "",
          exclude: aiExclude.trim(),
          pace: Number(paceMultiplier) || 1,
        })),
      };
    }
    // The trip-wide "Guide" group (see tripWideGroupIdRef in proceed(), which
    // runs before finalScheduleName is known here) needs the same real-
    // schedule-name fix as addMosaicGroupForDestination's per-destination
    // groups — set once, now that it's resolved. Found 2026-09-26.
    if (tripWideGroupIdRef.current != null) {
      const tg = window.guide_obj.groups.find((g) => g.id === tripWideGroupIdRef.current);
      if (tg) tg.schedule = finalScheduleName;
    }
    // createHTMLItinerary numbers/renders "Day N" purely from
    // window.guide_obj.schedule.events grouped by calendar date — a date
    // with zero events isn't a blank "Day N", it's not there at all, and
    // every day after it silently renumbers. noPlace/"Sea Day" destinations
    // are filtered out of validDestinations above and this wizard otherwise
    // only ever creates events for real places, so every sea day fell into
    // that gap. Give each one a real placeholder event instead.
    addNoPlacePlaceholderEvents(allDestinationsRef.current, finalScheduleName);
    await addWaypointPlacesAndEvents(allDestinationsRef.current, finalScheduleName);
    destinationQueueRef.current = validDestinations.slice();
    runNextDestination();
  };

  // AISearch's own onHide fires whether the background pipeline actually
  // finished (via its update_ui prop) or the user closed it early — either
  // way, whatever results exist (which may be none) are already in
  // SearchResultsPlacesList by now. pickPlacesAuto (collected up front in
  // the setup dialog) decides whether everything just gets added and
  // scheduled automatically, or the user reviews them on the Places screen
  // first — same branch the Google-sourced search effect below uses.
  const handleAISearchHide = () => {
    // See destCacheEligibleRef's own comment — an AI search WITH preferences
    // set produced personalized results that must never land in the shared
    // city+country-keyed cache.
    const prefs = window.guide_obj?.aiPreferences || {};
    destCacheEligibleRef.current = !(prefs.TripType || prefs.travelingWith || prefs.selectedBudget
      || prefs.selectedPace || prefs.selectedTransportation || prefs.focusOn || prefs.Activities
      || prefs.selectedAccessibility);
    // Same cross-destination exclusion the Google-sourced search effect
    // applies below — a repeat visit to a city the AI already suggested
    // places for this run would otherwise likely suggest overlapping ones
    // again (nothing else prompts it to avoid what's already been picked).
    const excludeKeys = scheduledPlaceKeysForSchedule(scheduleOptionsRef.current?.scheduleName);
    const fresh = SearchResultsPlacesList.filter((p) => !excludeKeys.has(placeKey(p)));
    if (fresh.length !== SearchResultsPlacesList.length) {
      dispatch(slice1Actions.setSearchResultsPlacesList({ value: fresh }));
    }
    if (!scheduleOptionsRef.current?.pickPlacesAuto) {
      updateUI();
      advancedRef.current = true;
      setStep(STEP_PICKING);
      enrichCandidatePhotos(fresh);
      return;
    }
    dispatch(slice1Actions.addAllResultsToGuide());
    updateUI();
    buildCurrentDestinationAndAdvance();
  };

  useEffect(() => {
    if (step === STEP_PICKING) goToPlaces();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Places source = Google — geocodes the current destination (currentDestRef,
  // set by runNextDestination), sources candidate places via
  // GooglePlaces.searchCityAttractions instead of an LLM call, then follows
  // the same pickPlacesAuto branch handleAISearchHide uses: review on the
  // Places screen, or add everything and build immediately using the
  // options collected at the setup dialog before the search even started.
  // The guide's AI category lists (AppSettings, else the defaults) — what a
  // search uses to give a found place this guide's category.
  const aiCategoryLists = () => {
    let aiCategories = DEFAULT_AI_CATEGORIES;
    let aiBaseCategories = DEFAULT_AI_BASE_CATEGORIES;
    try {
      const parsed = JSON.parse(AppSettings.AICategories);
      if (Array.isArray(parsed) && parsed.length > 0) aiCategories = parsed;
    } catch (e) { /* keep default */ }
    try {
      const parsed = JSON.parse(AppSettings.AIBaseCategories);
      if (Array.isArray(parsed) && parsed.length > 0) aiBaseCategories = parsed;
    } catch (e) { /* keep default */ }
    return { aiCategories, aiBaseCategories };
  };
  const aiPlanKey = (p) => `${String(p.Name || "").trim().toLowerCase()}|${String(p.City || "").trim().toLowerCase()}`;

  useEffect(() => {
    if (step !== STEP_GOOGLE_SEARCH) return;
    let cancelled = false;
    const startedAt = Date.now();
    const dest = currentDestRef.current;
    // Shared by both the cache-hit and live-search paths below — the only
    // difference between them is where rawPlaces comes from. fromCache
    // skips category remapping — a cache hit's places already went through
    // it once (this same block, the run that originally cached them, since
    // the cache is written post-enrichment from already-fully-processed
    // guide places), so re-running mapGoogleTypesToCategoryId on an already-
    // remapped real category ID (not Google's raw types anymore) would
    // silently produce garbage, same class of problem the existing
    // FEATURED-source skip below already guards against. Found 2026-09-27.
    const handleSearchResults = (rawPlaces, fromCache, cacheable) => {
      if (cancelled) return;
      // Google Places results are pure geography — never personalized — so
      // always safe to (re-)write into the shared cache. See
      // destCacheEligibleRef's own comment. Places taken from a guide
      // (cacheable === false) are NOT a search result for this city and must
      // not be served to a later Google build as if they were.
      destCacheEligibleRef.current = cacheable !== false;
      // Drop anything already scheduled under this same schedule (e.g. a
      // repeat visit to the same city, whether from an earlier
      // destination this run or an earlier "Build My Trip" run that
      // targeted the same schedule) before it ever reaches the guide —
      // otherwise the same top-rated places just get re-added and
      // re-scheduled on both visits.
      const excludeKeys = scheduledPlaceKeysForSchedule(scheduleOptionsRef.current?.scheduleName);
      const places = rawPlaces.filter((p) => !excludeKeys.has(placeKey(p)));
      if (!fromCache) {
        // place.Category still holds Google's raw types at this point
        // (parseNewAPIPlace's doing) — remap each to a single real guide
        // category ID before these places ever reach the guide/schedule.
        // Falls back to the canonical Common.js taxonomy (same fallback
        // AiSearch.js already uses) rather than an empty array — Build My
        // Trip's search can fire before HomeHeader.js's async settings
        // fetch populates AppSettings.AICategories/AIBaseCategories,
        // which previously meant every place silently matched nothing.
        let aiCategories = DEFAULT_AI_CATEGORIES;
        let aiBaseCategories = DEFAULT_AI_BASE_CATEGORIES;
        try {
          const parsed = JSON.parse(AppSettings.AICategories);
          if (Array.isArray(parsed) && parsed.length > 0) aiCategories = parsed;
        } catch (e) { /* keep default */ }
        try {
          const parsed = JSON.parse(AppSettings.AIBaseCategories);
          if (Array.isArray(parsed) && parsed.length > 0) aiBaseCategories = parsed;
        } catch (e) { /* keep default */ }
        // Featured-bucket places already carry a real category resolved by
        // parseFeaturedPlace (from the manifest's curated data) — their
        // Category is already a real guide category ID, not raw Google
        // types, so remapping them through mapGoogleTypesToCategoryId
        // would destroy it (a numeric ID string never fuzzy-matches a
        // category label).
        places.forEach((p) => {
          if (p.Source === Place_Source.FEATURED) return;
          p.Category = [mapGoogleTypesToCategoryId(p.Category, aiCategories, aiBaseCategories)].filter(Boolean);
        });
      }
      dispatch(slice1Actions.setSearchResultsPlacesList({ value: places }));
      setGoogleSearchElapsedMs(Date.now() - startedAt);
      if (!scheduleOptionsRef.current?.pickPlacesAuto) {
        updateUI();
        advancedRef.current = true;
        setStep(STEP_PICKING);
        enrichCandidatePhotos(places);
        return;
      }
      dispatch(slice1Actions.addAllResultsToGuide());
      // This destination's places = the search results as they now are in
      // the guide (2026-10-22). A return visit (Florence, Venice, Florence)
      // finds places the guide already has from the first visit — they
      // aren't "newly added", so the visit used to get none. Already-
      // scheduled ones were filtered out above.
      const resultKeys = new Set(places.map(placeKey));
      destSearchPlacesRef.current = (window.guide_obj?.places?.placesList || []).filter((p) => resultKeys.has(placeKey(p)));
      // state.places (what Places.js actually renders) only gets
      // recomputed via preFilterPlaces when setGuide fires — setting
      // SearchResultsPlacesList alone doesn't trigger that.
      updateUI();
      buildCurrentDestinationAndAdvance();
    };
    // Session-scoped cache (sessionStorage, keyed by city+country) — building
    // a second guide for a destination already searched earlier in the same
    // browser session re-ran the same paid Google Places search from
    // scratch. See project memory: project_session_place_search_cache_idea.
    // Shared with the AI places-source path (STEP_AI_SEARCH below) when that
    // path had no preferences set — see destCacheEligibleRef's own comment.
    // The normal source: the session cache, else a live Google search.
    const searchForPlaces = () => {
    const cachedPlaces = getCachedSearchResults(dest.city, dest.country);
    if (cachedPlaces) {
      handleSearchResults(cachedPlaces, true);
      return;
    }
    (async () => {
      try {
        // Check the curated citiesByCountry dataset first — most destinations
        // typed/picked here already match one of its entries, so this avoids
        // an unnecessary live geocode call when the coordinates are already
        // known. Matches mobile's BuildMyTripWizard.js _resolveLatLng.
        const cityMatch = (citiesByCountry[dest.country] || []).find((c) => c.city.toLowerCase() === dest.city.toLowerCase());
        let loc = cityMatch ? { lat: cityMatch.lat, lng: cityMatch.lon } : null;
        if (!loc) {
          const geoResp = await geocode(RequestType.ADDRESS, `${dest.city}, ${dest.country}`);
          loc = geoResp?.results?.[0]?.geometry?.location;
        }
        if (!loc) throw new Error("No location found for this destination");
        if (cancelled) return;
        const gp = new GooglePlaces();
        // keepImpracticalHours: true when the user chose to review places
        // manually — they might specifically want to see and decide about
        // a place with narrow hours, not have it silently disappear before
        // the picking screen ever renders. The automatic path keeps
        // stripping them, same as before.
        gp.searchCityAttractions(dest.city, dest.country, loc.lat, loc.lng, 20, (rawPlaces) => {
          if (cancelled) return;
          // Nothing came back because today's search limit was reached —
          // say so, instead of the generic "couldn't find places" (which
          // invites retrying something that can't work until tomorrow).
          if (takeDailyLimitNotice() && rawPlaces.length === 0) {
            setSearchLimitHit(true);
            setGoogleSearchError(LIMIT_MESSAGES.search.body);
            return;
          }
          // Not cached here (raw, pre-enrichment) — runEnrichment writes the
          // cache once these places are fully enriched instead, so a later
          // build actually gets the speedup. See its own comment.
          handleSearchResults(rawPlaces);
        }, !scheduleOptionsRef.current?.pickPlacesAuto);
      } catch (e) {
        if (cancelled) return;
        console.log("BuildMyTripWizard: Google Places search failed", e);
        setGoogleSearchError(`Couldn't find places for "${dest.city}, ${dest.country}." Please try again.`);
      }
    })();
    };
    // "From existing guides": this destination's places come from the
    // guide(s) picked for it. They're handed over exactly like a cache hit
    // (fully formed places, no category remapping) in importance order,
    // which the schedule build keeps — see GuidePlaceSource.js. Nothing
    // usable in the guides (or none picked) → the normal search above.
    // AI source: this destination's share of the trip's AI answer, each
    // place found by a search, then the usual results handling (review or
    // add). Their day/time hints go to aiPlanRef for the scheduler.
    const aiPlacesForDestination = async () => {
      const trip = aiTripRef.current;
      const result = await trip.promise;
      if (cancelled) return;
      if (result.error) {
        setGoogleSearchError(result.error);
        return;
      }
      const norm = (v) => String(v || "").trim().toLowerCase();
      let destIdx = trip.destinations.indexOf(dest);
      if (destIdx === -1) destIdx = trip.destinations.findIndex((d) => norm(d.city) === norm(dest.city) && norm(d.country) === norm(dest.country));
      const windows = planWindows(trip.destinations, {});
      const countryLevel = norm(dest.city) === norm(dest.country);
      const mine = result.entries.filter((e) => {
        if (!e.Day) return trip.destinations.length === 1 || norm(e.City) === norm(dest.city) || (countryLevel && norm(e.Country) === norm(dest.country));
        const w = windowFor({ day: e.Day, city: e.City, country: e.Country }, windows, trip.destinations);
        return !!w && w.destIdx === destIdx;
      });
      if (mine.length === 0) {
        handleSearchResults([], true, false);
        return;
      }
      let loc = null;
      try {
        const cityMatch = (citiesByCountry[dest.country] || []).find((c) => c.city.toLowerCase() === dest.city.toLowerCase());
        loc = cityMatch ? { lat: cityMatch.lat, lng: cityMatch.lon } : null;
        if (!loc) {
          const geoResp = await geocode(RequestType.ADDRESS, `${dest.city}, ${dest.country}`);
          loc = geoResp?.results?.[0]?.geometry?.location || null;
        }
      } catch (e) { /* search without a location bias */ }
      if (cancelled) return;
      const { aiCategories, aiBaseCategories } = aiCategoryLists();
      let done = 0;
      // A different step from "Choosing places" (the AI's choice): each chosen
      // place looked up on the map for its location, photo and hours. All
      // start at once and each takes a few seconds, so the count starts late.
      setAiStatusText(`Getting map details for ${dest.city}'s ${mine.length} places…`);
      const gp = new GooglePlaces();
      const found = await Promise.all(mine.map((e) => new Promise((resolve) => {
        gp.searchPlacesNewAPI(`${e.Attraction}, near ${e.City || dest.city} ${e.Country || dest.country}`, null, loc?.lat, loc?.lng, 10000, (results) => {
          done++;
          if (!cancelled) setAiStatusText(`Getting map details for ${dest.city}'s ${mine.length} places… (${done} / ${mine.length})`);
          resolve({ e, p: results && results.length > 0 ? results[0] : null });
        }, false, aiCategories, aiBaseCategories);
      })));
      if (cancelled) return;
      if (takeDailyLimitNotice() && found.every((x) => !x.p)) {
        setSearchLimitHit(true);
        setGoogleSearchError(LIMIT_MESSAGES.search.body);
        return;
      }
      const places = [];
      const seen = new Set();
      found.forEach(({ e, p }) => {
        if (!p) return; // not found — better left out than placed at a guessed spot
        p.Name = e.Attraction;
        p.City = e.City || dest.city;
        p.Country = e.Country || dest.country;
        const key = aiPlanKey(p);
        if (seen.has(key)) return;
        seen.add(key);
        if (e.Duration) { p.Duration = e.Duration; delete p.DurationUnset; }
        applyAIBestTime(p, e.BestTime); // never over a BestTime the place already has
        aiPlanRef.current.set(key, { day: e.Day || 0, time: e.BestTime || "any", city: e.City, country: e.Country, order: result.entries.indexOf(e) });
        places.push(p);
      });
      // fromCache=true: the categories are already this guide's (resolved by
      // the search above), not raw place types to map.
      handleSearchResults(places, true, false);
    };
    if (scheduleOptionsRef.current?.placeSource === "thisguide" && thisGuideByDestRef.current) {
      // The guide's own places for this destination — no search; leave out
      // any already in the target schedule.
      const idx = (scheduleOptionsRef.current.validDestinations || []).indexOf(dest);
      const excludeKeys = scheduledPlaceKeysForSchedule(scheduleOptionsRef.current?.scheduleName);
      const share = thisGuideByDestRef.current.get(idx) || [];
      // Copies for a new guide join it now. addPlace gives each a fresh ID;
      // the original goes back on, so links between them in their notes
      // still work (same as mobile).
      if (copySnapshotRef.current) {
        const placesModel = window.guide_obj.places;
        share.forEach((p) => {
          const original = p.ID;
          placesModel.addPlace(p);
          if (placesModel.placesList.includes(p) && p.ID !== original && placesModel.getPlace(original) == null) p.ID = original;
        });
      }
      thisGuideCurrentRef.current = share.filter((p) => !excludeKeys.has(placeKey(p)));
      destCacheEligibleRef.current = false;
      buildCurrentDestinationAndAdvance();
      return () => { cancelled = true; };
    }
    if (scheduleOptionsRef.current?.placeSource === "ai" && aiTripRef.current) {
      aiPlacesForDestination();
      return () => { cancelled = true; };
    }

    const guideSrc = scheduleOptionsRef.current?.guideSources?.[cityKey(dest.city, dest.country)];
    if (guideSrc && guideSrc.length > 0) {
      const readGuide = (id) => new Promise((resolve) => DataBase.readGuideTransaction(id, resolve, user_info.uid, { peek: true }));
      loadGuidePlaces(guideSrc, user_info.uid, window.guide_obj, readGuide).then(({ places, skipped }) => {
        if (cancelled) return;
        // Analytics: which guides trips are built from.
        guideSrc.filter((g) => !skipped.some((x) => x.guide_info_id === g.guide_info_id))
          .forEach((g) => logAnalyticsEvent("guide_build_from", { uid: user_info.uid || "guest", guide_id: g.guide_info_id }));
        skipped.forEach((g) => console.log(`BuildMyTripWizard: guide "${g.guide_name}" ${g.why} — skipped`));
        if (places.length === 0) { searchForPlaces(); return; }
        places.forEach((p) => guideSourcedPlacesRef.current.add(p));
        handleSearchResults(places, true, false);
      });
      return () => { cancelled = true; };
    }
    searchForPlaces();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, destRunToken]);

  // Places source = AI, but ONLY when no preferences are set — mirrors the
  // STEP_GOOGLE_SEARCH cache check above, reusing the same cache. An AI
  // search with no preferences produces the same kind of preference-agnostic
  // "top attractions in this city" result Google Places does, so it's safe
  // to serve from (and write into) that same cache; the render below skips
  // ever mounting the live AISearch component on a hit, so no LLM call
  // happens at all. When preferences ARE set, this effect does nothing and
  // STEP_AI_SEARCH's render falls through to the normal live AISearch path.
  // Requested 2026-09-27.
  useEffect(() => {
    if (step !== STEP_AI_SEARCH) return;
    const dest = currentDestRef.current;
    const prefs = window.guide_obj?.aiPreferences || {};
    const hasPrefs = !!(prefs.TripType || prefs.travelingWith || prefs.selectedBudget
      || prefs.selectedPace || prefs.selectedTransportation || prefs.focusOn || prefs.Activities
      || prefs.selectedAccessibility);
    if (hasPrefs) return;
    const cachedPlaces = getCachedSearchResults(dest.city, dest.country);
    if (!cachedPlaces) return;
    destCacheEligibleRef.current = true;
    const excludeKeys = scheduledPlaceKeysForSchedule(scheduleOptionsRef.current?.scheduleName);
    const fresh = cachedPlaces.filter((p) => !excludeKeys.has(placeKey(p)));
    dispatch(slice1Actions.setSearchResultsPlacesList({ value: fresh }));
    if (!scheduleOptionsRef.current?.pickPlacesAuto) {
      updateUI();
      advancedRef.current = true;
      setStep(STEP_PICKING);
      enrichCandidatePhotos(fresh);
      return;
    }
    dispatch(slice1Actions.addAllResultsToGuide());
    updateUI();
    buildCurrentDestinationAndAdvance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, destRunToken]);

  const goToPlaces = () => {
    if (MobileMode) {
      dispatch(slice1Actions.setCurrentAppScreen({ value: "Places" }));
    } else {
      dispatch(slice1Actions.setGuidesOrPlaces({ value: "Places", GuideType: "Mine" }));
      window.location.href = "#places-start";
    }
  };

  // Where the wizard actually lands once everything's built — now that it
  // populates state.homeScreen itself (see runNextDestination below), the
  // Itinerary/Home tab has real content to show instead of landing on
  // Schedule and making the user tap over to see the trip they just built.
  const goToItinerary = () => {
    if (MobileMode) {
      // Matches goToPlaces'/desktop's own setGuidesOrPlaces — App.js's render
      // gate for the Home tab checks CurrentAppScreen === "Home" AND
      // GuidesOrPlaces === "Places" together; without this, a run started
      // while GuidesOrPlaces was something else (e.g. from the Library/Guides
      // screen) would flip the screen but still fail that second check.
      dispatch(slice1Actions.setGuidesOrPlaces({ value: "Places", GuideType: "Mine" }));
      dispatch(slice1Actions.setCurrentAppScreen({ value: "Home" }));
    } else {
      dispatch(slice1Actions.setGuidesOrPlaces({ value: "Places", GuideType: "Mine" }));
      window.location.href = "#home-start";
    }
  };

  // Places this wizard run actually added — excludes anything that was
  // already in the guide before it opened, so appending to an existing trip
  // (adding a second leg/day) only ever schedules/counts the new places.
  const destSearchPlacesRef = useRef(null); // see handleSearchResults
  const getNewlyAddedPlaces = () =>
    (window.guide_obj?.places?.placesList || []).filter((p) => !existingPlaceIdsAtStartRef.current.has(p.ID));

  // Schedule/enrich options were already collected up front in the setup
  // dialog (before the search even ran) — this only reaches STEP_PICKING at
  // all when the user chose "let me pick the places", so there's no second
  // options dialog to show here regardless of source. Doesn't special-case
  // zero newly-added places (unlike closing the whole wizard used to) —
  // buildCurrentDestinationAndAdvance just moves on to the next destination,
  // or closes if this was the only/last one.
  const finishPicking = () => {
    buildCurrentDestinationAndAdvance();
  };

  // Skips manual picking entirely — adds every current search result to the
  // guide (same reducer action SearchResultsBanner's own "Add All" button
  // uses) and goes straight to the schedule-build step. Also refreshes
  // state.places for the same reason updateUI() is needed after the search
  // step itself (see the STEP_GOOGLE_SEARCH effect above).
  const addAllAndFinish = () => {
    dispatch(slice1Actions.addAllResultsToGuide());
    updateUI();
    finishPicking();
  };

  // Same callback shapes AISearch.js requires from every caller (PlacesHeader.js,
  // Map.js) — omitting any of these previously caused a real "app unresponsive"
  // bug (see project memory project_aisearch_places_menu_hang), since AISearch's
  // single-question flow silently falls into its background place-finding
  // pipeline whenever the AI's answer parses as a JSON array.
  const FindSearchResult = (name, id) => {
    let index = -1;
    if (name != null) index = SearchResultsPlacesList.findIndex((x) => x.Name === name);
    else index = SearchResultsPlacesList.findIndex((x) => x.ID === id);
    if (index === -1) return null;
    return SearchResultsPlacesList[index];
  };

  const AddSearchResult = (place) => {
    dispatch(slice1Actions.addToSearchResultsPlacesList({ value: [place] }));
  };

  const updateUI = () => {
    dispatch(slice1Actions.setGuide({ guide: null, fitToBounds: true }));
  };

  const SetScheduleName = (scheduleName) => {
    dispatch(slice1Actions.ClearAllFilters());
    dispatch(slice1Actions.setScheduleName({ value: scheduleName }));
    dispatch(slice1Actions.SetSchedulesFilter({ value: [{ label: scheduleName }] }));
    dispatch(slice1Actions.setGuide({ guide: null }));
  };

  // Opening-hours-aware event builder — used for every place regardless of
  // source, since Place.getOpeningHoursConflict treats missing hours data as
  // "unknown" and never blocks on it (only real "closed"/"closes_during"
  // conflicts trigger a shift), so this degrades gracefully for AI-suggested
  // places that don't have real Google hours data the way search results do.
  // See PlacesHeader.js's identical addEventOpeningHoursAware for the full
  // rationale. placesModel only needs a getPlace(id) method.
  // Extracted to Schedule.js as an instance method (2026-09-16) so it's a
  // plain, testable function instead of logic trapped inside this
  // component — see Schedule.js's addEventOpeningHoursAware for the actual
  // implementation. This thin wrapper keeps every existing call site below
  // unchanged.
  const addEventOpeningHoursAware = (place, itinerary, dayDate, lastPlace, roundingTo, firstEventOfDay, startH, endH, placesModel, maxAllowedDate, paceMultiplier, hardEndTime) =>
    window.guide_obj.schedule.addEventOpeningHoursAware(place, itinerary, dayDate, lastPlace, roundingTo, firstEventOfDay, startH, endH, placesModel, maxAllowedDate, paceMultiplier, hardEndTime);

  // scheduleOptions.scheduleName is resolved once, up front, in startBuild
  // (so every destination in a multi-city trip appends under the exact same
  // name rather than each one computing its own dedup) — see
  // PlacesHeader.js's buildScheduleFromGuidePlaces for the sibling
  // implementation this mirrors.
  const buildScheduleFromAddedPlaces = (scheduleOptions) => {
    // Only places this wizard run added since the last checkpoint — never
    // re-schedules places that were already in the guide (e.g. an earlier
    // day's, or an earlier destination's, places).
    const places = scheduleOptions?.placesOverride || getNewlyAddedPlaces();
    if (!places || places.length === 0) return;

    const scheduleName = scheduleOptions.scheduleName;
    const startH = GuideSettings?.schedule_day_start ?? 9;
    const endH = GuideSettings?.schedule_day_end ?? 20;
    const rounding_to = user_info.event_time_rounding ?? 15;

    // Select which places make the cut BEFORE geographic ordering,
    // preserving `places`' own incoming importance order — a Featured
    // manifest's curation rank, or GooglePlaces.searchCityAttractions's
    // Rating-descending sort, both already importance-ranked.
    // getPlaceOrderByDistance below otherwise discards that ranking
    // entirely, so "which N places fit the trip" ends up being "whichever
    // lands first in the geographically-sorted route" — a merely-nearby
    // but minor place (e.g. a stadium a few km out) can bump a genuinely
    // major one (a central cathedral) purely by chance of geography. Uses
    // each place's own Duration plus a flat per-stop travel buffer (real
    // travel time isn't known until the geographic order below exists —
    // this only needs to be a rough estimate of how many places fit, not
    // the final schedule) against the trip's TOTAL day-count budget, not
    // per-day, so importance wins across the whole trip rather than
    // stalling early because one place happened to fall near a day
    // boundary. No day-count cap (single-destination "let me pick" flow)
    // keeps every place, same as before. Found 2026-09-30 via a real Milan
    // Build My Trip that picked San Siro (a real but lower-ranked
    // attraction) over higher-ranked central landmarks purely because of
    // where it fell in the geographic route.
    const AVG_TRAVEL_BUFFER_MINUTES = 20;
    const dayCountForSelection = scheduleOptions && scheduleOptions.dayCount;
    let selectedPlaces = places;
    if (dayCountForSelection) {
      const totalBudgetMinutes = dayCountForSelection * (endH - startH) * 60;
      let cumulativeMinutes = 0;
      selectedPlaces = [];
      for (const place of places) {
        const placeMinutes = (place.Duration || 90) + AVG_TRAVEL_BUFFER_MINUTES;
        // Too long for what's left: skip it, but keep looking — a shorter
        // place further down may still fit (was `break`, which left a
        // Featured Cannes day at two events, 2026-10-17).
        if (cumulativeMinutes + placeMinutes > totalBudgetMinutes && selectedPlaces.length > 0) continue;
        cumulativeMinutes += placeMinutes;
        selectedPlaces.push(place);
      }
    }
    const roughlyExcluded = places.filter((p) => !selectedPlaces.includes(p));

    // Places with no visit length of their own get an estimate first.
    Common.estimateUnsetDurations(selectedPlaces, window.guide_obj.categories);
    const ordered = Common.getPlaceOrderByDistance(null, window.guide_obj, selectedPlaces);
    // Keep the device's local wall-clock date/time but relabel it as UTC
    // (setZone with keepLocalTime, NOT a real conversion) — Schedule display
    // code (e.g. SchedulePanel.js's day filter: DateTime.fromISO(e.Start, {
    // zone: "utc" })) reads Event.Start/End as UTC-tagged wall-clock time, so
    // a real local→UTC conversion here (shifting the actual numbers by the
    // user's UTC offset) both picks the wrong calendar day for "tomorrow" and
    // then gets shifted AGAIN on display.
    const startDate = (scheduleOptions && scheduleOptions.startDate) || (() => {
      const d = new Date();
      d.setDate(d.getDate() + 1);
      return d;
    })();
    let dayDate = DateTime.fromJSDate(startDate)
      .setZone("utc", { keepLocalTime: true })
      .set({ hour: startH, minute: 0, second: 0, millisecond: 0 });
    // If the destination's real arrival time (e.g. a cruise port's actual
    // arrival, from an Email Confirmation import) is later in the day than
    // the guide's normal day-start hour, don't schedule anything before the
    // traveler has actually arrived — start the first day there instead.
    const arrivalDateTime = scheduleOptions && scheduleOptions.arrivalDateTime;
    if (arrivalDateTime && arrivalDateTime.hasSame(dayDate, "day") && arrivalDateTime > dayDate) {
      dayDate = arrivalDateTime;
    }
    // Same idea for departure — the destination's stated To time is always
    // the hard boundary, for every destination. Only a genuine
    // transportation departure (applyDeparturePadding, set by
    // ScheduleDestinationsFlow.js's hasTransportTimes) also gets a boarding
    // buffer subtracted first: passengers need to be back before the ship
    // actually leaves, not scheduled up to the minute of departure itself.
    // A manually-entered or Explore-generated "To 6pm" has no such
    // reboarding margin implied, so it's used exactly as stated. Only
    // applies on the destination's actual last day —
    // addEventOpeningHoursAware checks hardEndTime against each candidate's
    // own day, not just this starting cursor.
    const DEPARTURE_PADDING_MINUTES = scheduleOptions?.applyDeparturePadding ? (scheduleOptions?.departurePaddingMinutes ?? 60) : 0;
    const departureDateTime = scheduleOptions && scheduleOptions.departureDateTime;
    const hardEndTime = departureDateTime ? departureDateTime.minus({ minutes: DEPARTURE_PADDING_MINUTES }) : null;
    let firstEventOfDay = true;
    let thisDay = dayDate;
    let lastPlace = null;
    // Optional day-count cap derived from the setup dialog's From/To dates — left
    // unset, this behaves exactly as before (fits every place regardless of
    // how many days it takes). maxAllowedDate is the first day OUTSIDE the
    // requested window; once the running cursor reaches it, every remaining
    // place is left unscheduled rather than silently spilling past it.
    const dayCap = scheduleOptions && scheduleOptions.dayCount;
    const maxAllowedDate = dayCap ? dayDate.startOf("day").plus({ days: dayCap }) : null;
    // "Leave room for lunch" — a real, visible placeholder event (no linked
    // Place, same convention TripAI.js's own AI-driven Lunch items use: a
    // Subject with no Places field) rather than a silent time-gap, so it's
    // something the user can actually see and later fill in with a real
    // restaurant. Inserted once per day, at the first moment the cursor
    // reaches noon — not tied to any place's location, so it adds no travel
    // time before/after (lastPlace is deliberately left untouched by this).
    const LUNCH_START_HOUR = 12;
    const LUNCH_DURATION_MINUTES = 60;
    const lunchPlacedDays = new Set();
    const maybeInsertLunch = (cursor) => {
      if (!scheduleOptions?.wantLunch) return cursor;
      const dayKey = cursor.toISODate();
      if (lunchPlacedDays.has(dayKey) || cursor.hour < LUNCH_START_HOUR) return cursor;
      const lunchEnd = cursor.plus({ minutes: LUNCH_DURATION_MINUTES });
      // No room left before a hard departure on this same day — skip rather
      // than schedule lunch right up against or past it.
      if (hardEndTime && cursor.hasSame(hardEndTime, "day") && lunchEnd > hardEndTime) return cursor;
      lunchPlacedDays.add(dayKey);
      let lunchEvent = new Appointment(window.guide_obj.schedule);
      lunchEvent.Subject = "Lunch";
      lunchEvent.Body = "";
      lunchEvent.Itinerary = scheduleName;
      lunchEvent.Start = cursor.toISO();
      lunchEvent.End = lunchEnd.toISO();
      window.guide_obj.schedule.addEvent(lunchEvent);
      return lunchEnd;
    };
    let placedCount = 0;
    // Actual place objects left out, not just a count — lets the "remove
    // them?" prompt below actually delete them instead of just reporting.
    // Seeded with whatever the importance-based rough selection above
    // already excluded, so the final "scheduled X of Y" count and the
    // remove-unused prompt still account for every candidate place, not
    // just the ones that made it into `ordered`.
    const skippedPlaces = [...roughlyExcluded];
    // Shared with the Featured-guide fix tools (Common/schedulePlacesInOrder.js):
    // places in route order inside their opening hours, and when the next one
    // is closed today, the next one that's open goes first (2026-10-30 — before,
    // a closed first place left the trip's first day empty).
    const run = schedulePlacesInOrder(window.guide_obj.schedule, ordered, {
      scheduleName, dayDate, startH, endH, roundingTo: rounding_to, placesModel: window.guide_obj.places,
      maxAllowedDate, paceMultiplier: scheduleOptions?.paceMultiplier, hardEndTime, beforeEach: maybeInsertLunch,
      firstEventOfDay, lastPlace, thisDay,
    });
    ({ dayDate, firstEventOfDay, lastPlace, thisDay } = run);
    placedCount += run.placed.length;
    skippedPlaces.push(...run.skipped);

    // Fill the time left at the end with places that didn't make it — the
    // budget above is only a rough guess, and a place that turned out not to
    // fit (a 6-hour visit, a closed day) otherwise leaves the rest of the
    // day empty. Most important first. 2026-10-17.
    for (const place of places.filter((p) => p && skippedPlaces.includes(p))) {
      if (maxAllowedDate && dayDate.startOf("day") >= maxAllowedDate) break;
      const result = addEventOpeningHoursAware(place, scheduleName, dayDate, lastPlace, rounding_to, firstEventOfDay, startH, endH, window.guide_obj.places, maxAllowedDate, scheduleOptions?.paceMultiplier, hardEndTime);
      if (result === null) continue;
      skippedPlaces.splice(skippedPlaces.indexOf(place), 1);
      dayDate = result;
      placedCount++;
      if (dayDate.day !== thisDay.day) {
        thisDay = dayDate;
        firstEventOfDay = true;
      } else firstEventOfDay = false;
      lastPlace = place;
    }

    SetScheduleName(scheduleName);
    markGuestUnsaved();
    if (skippedPlaces.length > 0) {
      const skippedCount = skippedPlaces.length;
      // Never auto-deletes places anymore — see PlacesHeader.js's
      // RemoveUnusedPlaces (Places menu) for the equivalent on-demand
      // cleanup. Simplified 2026-09-14.
      console.log(
        `BuildMyTripWizard: scheduled ${placedCount} of ${placedCount + skippedCount} places in ${dayCap} day${dayCap === 1 ? "" : "s"}; left ${skippedCount} unscheduled in the guide.`
      );
    }
  };

  // Wikipedia lookup, ported verbatim from PlacesHeader.js's
  // processInfoQueue worker — including the skip when the place already has
  // our own Wikipedia-sourced Info (detected by the exact marker link this
  // same lookup inserts, not just "any Info"), so re-running this is safe.
  // A plain `await fetch()` here never rejects on a hang — it just never
  // resolves, so the outer try/catch below gave zero protection against
  // exactly that. If enrichPlaceInfo runs in the same bounded worker pool
  // as enrichPlaceHours/enrichPlacePhoto (ENRICH_CONCURRENCY below), one
  // stuck request permanently occupies a worker slot. Matches the
  // AbortController pattern already used elsewhere (GooglePlaces.js's
  // searchPlacesNewAPI, WikiMedia.js/GoogleSearch.js/Flickr.js's photo
  // fetchers) — real report: TripAI's own photo chain stuck at 3/31 from
  // this exact class of bug. Found/prioritized 2026-09-05.
  const fetchWithTimeout = (url, ms = 10000) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), ms);
    return fetch(url, { signal: controller.signal }).finally(() => clearTimeout(timeoutId));
  };

  const enrichPlaceInfo = async (place) => {
    if (place.Info && place.Info.includes("See more on Wikipedia")) return;
    // A place copied from a guide keeps that guide's notes.
    if (guideSourcedPlacesRef.current.has(place) && String(place.Info || "").trim() !== "") return;
    const query = [place.Name, place.City, place.Country].filter(Boolean).join(", ");
    try {
      const searchUrl = `https://en.wikipedia.org/w/api.php?action=query&list=search&srsearch=${encodeURIComponent(query)}&format=json&origin=*&srlimit=1`;
      const searchResp = await fetchWithTimeout(searchUrl);
      const searchData = await searchResp.json();
      const results = searchData?.query?.search;
      if (results && results.length > 0) {
        const title = results[0].title;
        const summaryUrl = `https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(title)}`;
        const summaryResp = await fetchWithTimeout(summaryUrl);
        const summary = await summaryResp.json();
        const extract = summary.extract || "";
        const pageUrl = summary.content_urls?.desktop?.page || `https://en.wikipedia.org/wiki/${encodeURIComponent(title)}`;
        if (extract) {
          place.Info = `${extract}<br><a href="urllink:${pageUrl}">... See more on Wikipedia</a>`;
        }
      }
    } catch (_) {
      // silently skip on error
    }
  };

  // Google opening-hours lookup, ported from PlacesHeader.js's
  // processOpeningHoursQueue worker — same skip-if-already-enriched via
  // hasOpeningHoursData (not just truthiness, since every place starts with
  // an empty default OpeningHours skeleton).
  // A place Google has no hours for (a square, a bridge) comes back empty,
  // so "has no hours" can't tell "never asked" from "asked, none exist" —
  // without this, such a place was looked up before scheduling AND again in
  // the detail step, a second paid call for the same empty answer. One
  // attempt per place per wizard run. 2026-10-12.
  const hoursTriedRef = useRef(new WeakSet());
  const enrichPlaceHours = (place) =>
    new Promise((resolve) => {
      if (GooglePlaces.hasOpeningHoursData(place.OpeningHours) || hoursTriedRef.current.has(place)) {
        resolve();
        return;
      }
      hoursTriedRef.current.add(place);
      try {
        GooglePlaces.fetchOpeningHours(place, (hours) => {
          if (hours) place.OpeningHours = hours;
          resolve();
        });
      } catch (_) {
        resolve();
      }
    });

  // Photo, via the same cheap Custom Search → Wikimedia → Flickr chain
  // PlacesHeader.js's "Auto Set Photo" bulk action already uses — NOT Places
  // API (New), which is deliberately excluded from the search field mask now
  // (see google_places_new_search.php) since a live Places Photo Media URL
  // gets re-billed by Google on every future view, by every user, forever.
  // Unconditional (not gated by an enrich toggle, unlike info/hours below) —
  // every Google-sourced place needs SOME photo rather than the generic
  // placeholder, so this isn't "extra" optional detail the way Wikipedia
  // info or opening hours are.
  const enrichPlacePhoto = (place) =>
    new Promise((resolve) => {
      if (hasRealPhoto(place)) {
        resolve();
        return;
      }
      try {
        PlaceMenu.autoSetPhoto(place, () => resolve());
      } catch (_) {
        resolve();
      }
    });

  // "Add opening times" / "Auto add info to Notes" — the same two bulk
  // Content-menu actions available later from the Places menu, offered here
  // as an optional, skippable step right after the wizard adds places, so
  // new guides start already enriched instead of the user having to
  // discover these in Advanced Mode afterward. Runs a bounded pool of
  // concurrent workers (matching PlacesHeader.js's processInfoQueue/
  // processOpeningHoursQueue) instead of one place at a time with an
  // artificial pause between each — network latency, not any real
  // per-place work, dominates each lookup, so this is a straightforward
  // throughput win.
  const ENRICH_CONCURRENCY = 4;

  // "Let me pick the places myself" — the picking screen otherwise shows
  // every candidate with the generic placeholder image (enrichPlacePhoto
  // normally only runs in the final runEnrichment pass, over places already
  // ADDED to the guide, well after picking is done). Fires in the
  // background as soon as candidates arrive — doesn't block the transition
  // to STEP_PICKING — and nudges the UI to re-render as each photo resolves,
  // so real photos "pop in" progressively instead of the user waiting on
  // all of them before seeing anything. Same cheap Custom Search/Wikimedia/
  // Flickr chain as enrichPlacePhoto, not Places API — this fetches photos
  // for every candidate shown, including ones never picked, which is the
  // deliberate trade-off for a real picking experience.
  const enrichCandidatePhotos = async (candidatePlaces) => {
    const needPhoto = candidatePlaces.filter((p) => !hasRealPhoto(p));
    if (needPhoto.length === 0) return;
    const workerCount = Math.min(ENRICH_CONCURRENCY, needPhoto.length);
    let nextIndex = 0;
    const runWorker = async () => {
      while (nextIndex < needPhoto.length) {
        const place = needPhoto[nextIndex++];
        await enrichPlacePhoto(place);
        updateUI();
      }
    };
    await Promise.all(Array.from({ length: workerCount }, runWorker));
  };

  const runEnrichment = async (wantInfo, wantHours) => {
    advancedRef.current = true;
    // Only places THIS run added (runAddedPlaceIdsRef), not the guide's
    // entire placesList — a guide with lots of pre-existing places
    // shouldn't re-run paid photo/info/hours lookups over places that have
    // nothing to do with this run. Falls back to the guide's WHOLE
    // placesList when the tracked set comes back empty despite the run
    // clearly having added real places (runAddedPlaceIdsRef is populated
    // per-destination as each search resolves — found 2026-09-15 via a
    // brand-new guide that built real places but never got enrichment/a
    // cover photo, matching a miss in that tracking rather than a genuine
    // "nothing was added" case). Safe even for a large pre-existing guide:
    // enrichPlacePhoto/Info/Hours each already skip a place that's already
    // enriched, so the fallback only does real work on places that need it.
    const trackedPlaces = (window.guide_obj?.places?.placesList || []).filter((p) => runAddedPlaceIdsRef.current.has(p.ID));
    // "This guide's places": no photo/info enrichment — those places, their
    // notes and the guide's cover are the user's; their missing hours were
    // already looked up before scheduling.
    const places = scheduleOptionsRef.current?.placeSource === "thisguide"
      ? []
      : trackedPlaces.length > 0 ? trackedPlaces : (window.guide_obj?.places?.placesList || []);
    // Places taken from a guide that didn't make it into the schedule don't
    // get an hours lookup here: a guide has 20-35 places, most unscheduled,
    // and looking them all up on every build was slow and spent a large
    // share of a user's daily lookup allowance on places that may never be
    // used. Swap/Replace looks hours up on demand for the few it offers
    // (SchedulePanel.js's checkReplaceCandidateHours); "Find Opening Times"
    // covers the rest. 2026-10-13.
    const scheduledIds = new Set();
    (window.guide_obj?.schedule?.events || []).forEach((e) =>
      String(e.Places || "").split(",").map((id) => id.trim()).filter(Boolean).forEach((id) => scheduledIds.add(id)));
    const skipHours = (place) => guideSourcedPlacesRef.current.has(place) && !scheduledIds.has(String(place.ID));
    if (places.length > 0) {
      enrichAbortRef.current = false;
      setStep(STEP_ENRICHING);
      const total = places.length;
      let nextIndex = 0;
      let completed = 0;
      const runWorker = async () => {
        while (nextIndex < total) {
          if (enrichAbortRef.current) return;
          const place = places[nextIndex++];
          // Labeled by phase (not by provider — several different services can
          // back any of these depending on settings/availability) so the pill
          // reads as "what it's doing", not just a bare counter.
          setEnrichProgress(`Fetching photos: ${completed} / ${total}`);
          await enrichPlacePhoto(place);
          if (enrichAbortRef.current) return;
          if (wantInfo) {
            setEnrichProgress(`Adding info: ${completed} / ${total}`);
            await enrichPlaceInfo(place);
          }
          if (enrichAbortRef.current) return;
          if (wantHours && !skipHours(place)) {
            setEnrichProgress(`Checking hours: ${completed} / ${total}`);
            await enrichPlaceHours(place);
          }
          completed++;
          // Re-label with whichever phase this place actually finished on
          // (hours if requested, else info, else photos) instead of dropping
          // back to a bare count.
          const lastLabel = wantHours ? "Checking hours" : wantInfo ? "Adding info" : "Fetching photos";
          setEnrichProgress(`${lastLabel}: ${completed} / ${total}`);
        }
      };
      const workerCount = Math.min(ENRICH_CONCURRENCY, total);
      await Promise.all(Array.from({ length: workerCount }, runWorker));
      setEnrichProgress("");
    }
    // Update the session place search cache (SessionPlaceSearchCache.js)
    // with the now-FULLY-ENRICHED places, per destination — the earlier
    // cache write (right after the raw search, in the STEP_GOOGLE_SEARCH
    // effect) only had pre-enrichment data, so a later build reusing it
    // still had to re-run photo/info/hours lookups from scratch, delivering
    // no real speedup (search itself isn't the slow part). Overwrites that
    // same sessionStorage entry with the enriched version. `places` here is
    // the whole run's combined places across every destination, so split it
    // back out using destinationPlaceIdsRef. Requested 2026-09-27.
    const placeById = new Map(places.map((p) => [p.ID, p]));
    for (const { city, country, placeIds, cacheEligible } of Object.values(destinationPlaceIdsRef.current)) {
      // Skip AI-sourced destinations that were searched WITH preferences set
      // — see destCacheEligibleRef's own comment. Google-sourced and
      // no-preference AI-sourced destinations share one cache; personalized
      // AI results never enter it.
      if (cacheEligible === false) continue;
      const destPlaces = placeIds.map((id) => placeById.get(id)).filter(Boolean);
      if (destPlaces.length > 0) setCachedSearchResults(city, country, destPlaces);
    }
    // Cover pick, itinerary rebuild, and the closing dispatch/save all run
    // unconditionally now (no early `closeWizard(); return;` above) — a
    // guide that visibly got real places built into it should never
    // silently end up coverless just because runAddedPlaceIdsRef missed
    // some or all of them. `places` already includes the whole-guide
    // fallback from above when that tracking came back empty.
    // Guide cover photo — a user-set image (Guide Settings' "Change image")
    // is left alone; auto-picking only kicks in when the guide doesn't
    // already have a real one, so the user stays in control of it instead
    // of it being silently overwritten on every Build My Trip run. When
    // there isn't one yet, use the first place that actually got a real
    // photo — same "just pick one, don't overthink it" spirit as TripAI's
    // own incidental "whichever finished last" choice. Ported to mobile
    // 2026-08-30; user-image precedence added there and here the same day.
    // A stale, pre-fix cached copy of a guide getting saved again can bring
    // back a live Google Places (New) Photo Media URL even after it was
    // already remediated in Firestore — rejecting that pattern here too
    // (not just the plain placeholder) keeps this "respect the custom
    // cover" check from treating a leaked billing URL as the user's
    // intentional choice. Found 2026-08-30 via on-device testing.
    const isBadCoverUrl = (url) =>
      !url || url === "https://andiamotravelapp.com/Images/App/IllustratedImage.jpg" || /places\.googleapis\.com\/v1\/.*\/media/i.test(url);
    const guideHasCustomCover = !isBadCoverUrl(window.guide_obj.attributes.image_m);
    // "Replace this guide"/"new guide" targets: the guide's content just got
    // wiped and rebuilt from scratch, but the clear step deliberately
    // preserves the OLD image_m, so guideHasCustomCover reads true even
    // though that cover belongs to whatever the guide used to be about, not
    // this newly-built trip. Only "add to an existing guide" should respect
    // an existing custom cover. Same fix as TripAI.js and mobile's
    // BuildMyTripWizard.js/PlacesScreen.js. Found 2026-09-26.
    const shouldPreserveExistingCover = guideTarget === "add" && guideHasCustomCover;
    // Skip the first real-photo place when a second one exists — that first
    // place's own photo is often ALSO what a mosaic tile (Guide.js's
    // createHTMLItinerary) or the itinerary's first listed place ends up
    // showing, since those are picked the same "first with a real photo"
    // way. Picking a different place here avoids the guide's own cover
    // looking like a duplicate of content shown right next to/after it.
    // Still falls back to the first (only) candidate when there's just one.
    // Requested 2026-09-27.
    // A new guide made from this guide's places has none of its own
    // (`places` is empty for that source): take the cover from the copies.
    const coverCandidates = places.length === 0 && copySnapshotRef.current ? (window.guide_obj?.places?.placesList || []) : places;
    const photoPlaces = shouldPreserveExistingCover ? [] : coverCandidates.filter((p) => hasRealPhoto(p));
    const coverPlace = photoPlaces.length > 1 ? photoPlaces[1] : (photoPlaces[0] || null);
    if (coverPlace) {
      let guide_attributes = JSON.parse(JSON.stringify(window.guide_obj.attributes));
      guide_attributes.image = coverPlace.Photo || coverPlace.Photo_m;
      guide_attributes.image_m = coverPlace.Photo_m || coverPlace.Photo;
      window.guide_obj.attributes.modifyGuideAttributes(guide_attributes);
    }
    // Rebuild the itinerary HTML now that photos (and info/hours) are
    // actually populated — the version built in runNextDestination, before
    // this ran, has every place's photo baked in empty.
    if (builtOwnItineraryRef.current) {
      builtOwnItineraryRef.current = false;
      const html_itin_String = window.guide_obj.createHTMLItinerary(scheduleOptionsRef.current?.scheduleName, { info: true, photo: true, directions: true, place_menu: true, eventBody: false });
      // Same cover-image layout as TripAI.js's finalUpdate — reflects
      // whatever attributes.image_m ends up as (user's custom cover or the
      // just-picked coverPlace), not just the latter.
      const finalCoverUrl = window.guide_obj.attributes.image_m;
      // isBadCoverUrl already screens out genuinely missing/placeholder/
      // billing-URL covers (renders nothing at all for those, per design).
      // What slipped through it: a coverPlace's Photo_m can be a raw
      // gdrive:FILEID sentinel or a bare relative filename — neither is a
      // usable <img src> on its own, so it rendered as a broken image icon
      // despite Guide Info showing nothing wrong (its own display path runs
      // this same conversion, this embed didn't). Common.getImageURL is the
      // established fix for exactly this class of bug — see ScheduleHeader.js's
      // "Copy to Trip Itinerary" cover-photo fix. Found 2026-09-14.
      const coverImg = !isBadCoverUrl(finalCoverUrl)
        ? Common.coverImageHTML(Common.getImageURL(finalCoverUrl, null, true))
        : "";
      window.guide_obj.document = `${coverImg}<p><h3 style="text-align:center;">${window.guide_obj.attributes.guide_name}</h3></p></br>${html_itin_String}`;
      // Sync state.ScheduleName before resetHomeScreen — see the identical
      // comment above this run's earlier resetHomeScreen call.
      dispatch(slice1Actions.setScheduleName({ value: scheduleOptionsRef.current?.scheduleName }));
      dispatch(slice1Actions.resetHomeScreen({ value: null }));
    }
    dispatch(slice1Actions.setGuide({ guide: null }));
    markGuestUnsaved();
    // Today's lookup limit was reached while adding opening times (before
    // scheduling or in the detail pass above): say so once, now that the
    // trip is on screen. See Common/GateLimit.js.
    if (takeDailyLimitNotice()) showLimitNotice("hours");
    closeWizard();
  };

  // Builds the CURRENT destination's schedule (date-bounded to just that
  // destination's own From/To) using the options collected up front in the
  // setup dialog, advances the "newly added" checkpoint past what it just
  // scheduled, then moves on to the next destination in the queue — or, once
  // the queue is empty, runs a single enrichment pass over everything added
  // across every destination and closes. No separate "add more detail?"
  // ask, and for the fully-automatic default, no manual picking screen
  // either. Reached either right after a destination's search (auto path)
  // or after finishPicking (when the user chose "let me pick the places").
  //
  // Real opening hours are fetched for this destination's places BEFORE the
  // schedule build runs, not after — Google search results don't always
  // include regularOpeningHours, and the final enrichment pass (which would
  // otherwise fill that in) only runs once, at the very end, well after
  // every destination's schedule is already committed. Without this,
  // addEventOpeningHoursAware sees "unknown" for those places (correctly
  // non-blocking by design) and schedules them freely, only for the real
  // hours to surface a conflict afterward that nothing then re-checks.
  // Mosaic group creation, mirroring TripAI.js's group-tagging pattern on
  // mobile — without a mosaic-flagged group (or a non-"Main" schedule name,
  // which the "new guide"/"replace guide" flow never uses, see
  // finalScheduleName above), a Build My Trip guide's Photo Mosaic view
  // stays permanently empty even with a fully populated schedule. Web's own
  // TripAI.js has no equivalent group-tagging at all, so it's exposed to
  // the same gap — this wizard is fixed first since that's what was
  // actually reported. One group per destination, matching this wizard's
  // existing per-destination loop. Found 2026-09-03.
  const addMosaicGroupForDestination = (dest, places) => {
    const cityBase = (dest.city || dest.country || "Trip").trim().substring(0, 20);
    const targetSchedule = scheduleOptionsRef.current?.scheduleName || cityBase;
    let groupName = cityBase;
    let tripGroupId;
    const sameNameGroup = window.guide_obj.findGroupFromName(groupName);
    // Reuse a same-named mosaic group already tied to this exact schedule —
    // this destination getting queued again (built should prevent that, but
    // this is the actual damage when it somehow doesn't) would otherwise
    // mint a cosmetic "-2" duplicate group instead of just adding these
    // places to the group that already represents this destination. Only
    // disambiguate with -2 when the existing group is for a genuinely
    // different schedule (e.g. a repeat visit to the same city on a
    // separate trip). Found 2026-09-27.
    if (sameNameGroup !== null && sameNameGroup.mosaic && sameNameGroup.schedule === targetSchedule) {
      tripGroupId = sameNameGroup.id;
    } else {
      if (sameNameGroup !== null) {
        let n = 2;
        while (window.guide_obj.findGroupFromName(`${cityBase}-${n}`) !== null) n++;
        groupName = `${cityBase}-${n}`;
      }
      tripGroupId = window.guide_obj.addGroup(groupName);
    }
    const tripGroup = window.guide_obj.groups.find((g) => g.id === tripGroupId);
    // poi_img 16 ("big city") — same convention already used elsewhere for
    // destination groups (HomeHeader.js/MobileBottomNavBar.js's own
    // `group.poi_img = 16`). Requested 2026-09-26.
    //
    // .schedule must be the REAL schedule name these places actually landed
    // in (Home.js's mosaic-tile click handler filters by group AND by
    // schedule===this value — see SetSchedulesFilter there), not the
    // group's own display name. Every group created here used the group
    // name instead, so tapping any TripAI/Build My Trip mosaic tile filtered
    // by a schedule that never existed and showed zero places. Found
    // 2026-09-26 — reported as "no places displayed" when clicking a photo.
    if (tripGroup) { tripGroup.schedule = scheduleOptionsRef.current?.scheduleName || groupName; tripGroup.mosaic = true; tripGroup.poi_img = 16; }
    places.forEach((p) => {
      // Defensive — TripAI.js's equivalent mosaic-group step crashed on a
      // place missing .Group entirely (a bare object literal instead of a
      // real Place instance), killing its whole loop silently. Nothing in
      // this wizard currently feeds it anything but real Place instances,
      // but the pattern is identical and cheap to guard. Found 2026-09-05.
      if (tripGroupId !== null && !(p.Group || []).includes(tripGroupId)) {
        p.Group = p.Group || [];
        p.Group.push(tripGroupId);
      }
      // Whole-trip "Guide" group, on top of the per-destination one above —
      // see tripWideGroupIdRef's creation in proceed(). null for a
      // single-destination trip. Requested 2026-09-26.
      const tripWideId = tripWideGroupIdRef.current;
      if (tripWideId !== null && !(p.Group || []).includes(tripWideId)) {
        p.Group = p.Group || [];
        p.Group.push(tripWideId);
      }
    });
  };

  // Marks this destination "done" directly on the actual persisted array
  // (window.guide_obj.destinations) — not just on the in-memory dest object
  // this wizard is holding, since cleanDestination already wrote a fresh,
  // decoupled copy of every destination onto the guide back in startBuild(),
  // before any of them were actually searched/scheduled. Matched by id, not
  // city, so two legitimately separate destinations sharing a city name
  // (a trip that revisits a place) aren't confused for each other. See
  // cleanDestination's comment for why this exists.
  const markDestinationBuilt = (destId) => {
    const entry = (window.guide_obj.destinations || []).find((d) => d.id === destId);
    if (entry) { entry.built = true; delete entry.builtAs; }
  };

  const buildCurrentDestinationAndAdvance = async () => {
    const dest = currentDestRef.current;
    // "This guide's places": the places were already in the guide, so they
    // aren't "newly added" — use this destination's share, and leave the
    // guide's own groups as they are.
    const fromThisGuide = scheduleOptionsRef.current?.placeSource === "thisguide";
    const newlyAdded = fromThisGuide ? thisGuideCurrentRef.current : (destSearchPlacesRef.current || getNewlyAddedPlaces());
    destSearchPlacesRef.current = null;
    if (!fromThisGuide) addMosaicGroupForDestination(dest, newlyAdded);
    // Opening hours are looked up BEFORE scheduling so the build can avoid
    // closed places. Google search results arrive with hours, so this is
    // normally a no-op — but places taken from a guide ("From existing
    // guides") almost never have any, and looking up every one of a
    // 30-place guide took ~25s (and a paid lookup each) before a single
    // event was scheduled. For a guide-sourced destination, only the places
    // that can actually fit its days are looked up (same rough budget
    // buildScheduleFromAddedPlaces uses, plus a few spares for ones that get
    // skipped), with more lookups in flight at once — and none at all when
    // "Add opening times" is switched off. Found 2026-10-12.
    const fromGuide = !!scheduleOptionsRef.current?.guideSources?.[cityKey(dest.city, dest.country)]?.length;
    let hoursCandidates = newlyAdded;
    if (fromGuide) {
      if (!scheduleOptionsRef.current?.enrichHoursWanted) hoursCandidates = [];
      else {
        const startH = GuideSettings?.schedule_day_start ?? 9;
        const endH = GuideSettings?.schedule_day_end ?? 20;
        const days = Math.round(dest.dateEnd.startOf("day").diff(dest.dateStart.startOf("day"), "days").days) + 1;
        const budget = days * (endH - startH) * 60;
        let used = 0, fits = 0;
        for (const p of newlyAdded) {
          used += (p.Duration || 90) + 20;
          if (used > budget && fits > 0) break;
          fits++;
        }
        hoursCandidates = newlyAdded.slice(0, fits + 3);
      }
    }
    const needHours = hoursCandidates.filter((p) => !GooglePlaces.hasOpeningHoursData(p.OpeningHours));
    if (needHours.length > 0) {
      const workerCount = Math.min(fromGuide ? ENRICH_CONCURRENCY * 2 : ENRICH_CONCURRENCY, needHours.length);
      let nextIndex = 0;
      const runWorker = async () => {
        while (nextIndex < needHours.length) {
          await enrichPlaceHours(needHours[nextIndex++]);
        }
      };
      await Promise.all(Array.from({ length: workerCount }, runWorker));
    }

    const dayCount = Math.round(dest.dateEnd.startOf("day").diff(dest.dateStart.startOf("day"), "days").days) + 1;
    if (scheduleOptionsRef.current?.placeSource === "ai" && aiTripRef.current) {
      // The AI's day and time hints decide the order (aiDaySchedule.js):
      // each day inside its window, morning first, sunset/evening last,
      // meals at meal times, closed places moved, spare time topped up with
      // this city's extras; whatever doesn't fit stays unscheduled.
      setAiStatusText(`Building the schedule for ${dest.city}…`);
      const entries = newlyAdded
        .map((p) => {
          const plan = aiPlanRef.current.get(aiPlanKey(p));
          return plan ? { place: p, day: plan.day, time: plan.time, city: plan.city, country: plan.country, order: plan.order } : null;
        })
        .filter(Boolean)
        .sort((a, b) => a.order - b.order);
      const scheduleName = scheduleOptionsRef.current.scheduleName;
      buildAIDaySchedule({
        entries,
        destinations: aiTripRef.current.destinations,
        schedule: window.guide_obj.schedule,
        places: window.guide_obj.places,
        itinerary: scheduleName,
        startH: GuideSettings?.schedule_day_start ?? 9,
        endH: GuideSettings?.schedule_day_end ?? 20,
        roundingTo: user_info?.event_time_rounding ?? 15,
        pace: scheduleOptionsRef.current.paceMultiplier,
        meals: scheduleOptionsRef.current.aiMeals || {},
        newEvent: () => new Appointment(window.guide_obj.schedule),
      });
      SetScheduleName(scheduleName);
      markGuestUnsaved();
    } else buildScheduleFromAddedPlaces({
      ...scheduleOptionsRef.current,
      placesOverride: fromThisGuide ? newlyAdded : null,
      startDate: Common.JSDateFromLuxonDate(dest.dateStart),
      dayCount,
      // The destination card's From/To are real, user-visible/editable
      // date-TIME fields (not just dates) — whatever time is set there
      // should always bound that day's scheduling, for every destination
      // regardless of source: nothing before the stated From time, nothing
      // after the stated To time. hasTransportTimes (set only by
      // ScheduleDestinationsFlow.js, for destinations derived from a real
      // schedule/confirmation) controls just the EXTRA safety buffer on top
      // of that stated departure time — see DEPARTURE_PADDING_MINUTES below
      // — since only a genuine transportation departure needs a "be there
      // early" margin; a manually-entered or Explore-generated "To 6pm"
      // just means free until 6pm, no reboarding buffer implied.
      arrivalDateTime: dest.dateStart,
      departureDateTime: dest.dateEnd,
      applyDeparturePadding: !!dest.hasTransportTimes,
    });
    // No manual bookkeeping needed for the next destination's exclusion set
    // — scheduledPlaceKeysForSchedule reads window.guide_obj.schedule.events
    // fresh each time, and buildScheduleFromAddedPlaces just committed this
    // destination's events into that same schedule. A place that didn't fit
    // never got an event in the first place, so it's correctly still
    // available to a later destination that might have room for it.
    // Union this destination's newly-added places into the whole-run set
    // before rolling existingPlaceIdsAtStartRef forward, so runEnrichment
    // can later re-enrich everything this run added, not just the last
    // destination's places.
    markDestinationBuilt(dest.id);
    getNewlyAddedPlaces().forEach((p) => runAddedPlaceIdsRef.current.add(p.ID));
    // Record which of this run's places belong to THIS destination — see
    // destinationPlaceIdsRef's own comment above.
    destinationPlaceIdsRef.current[destinationCacheKey(dest.city, dest.country)] = {
      city: dest.city,
      country: dest.country,
      placeIds: getNewlyAddedPlaces().map((p) => p.ID),
      cacheEligible: destCacheEligibleRef.current,
    };
    // Snapshot forward so the next destination's "newly added" set doesn't
    // include places this destination just scheduled.
    existingPlaceIdsAtStartRef.current = new Set((window.guide_obj?.places?.placesList || []).map((p) => p.ID));
    runNextDestination();
  };

  const skipEnrichment = () => {
    enrichAbortRef.current = true;
    closeWizard();
  };

  if (!props.show) return null;

  if (destError !== "") {
    return (
      <AlertModal
        show={true}
        onHide={() => setDestError("")}
        AlertModalTitle="Destination not found"
        AlertModalMessage={destError}
        AlertModalButtons={[{ Title: "OK", Method: null }]}
      />
    );
  }

  if (destValidationError !== "") {
    return (
      <AlertModal
        show={true}
        onHide={() => setDestValidationError("")}
        AlertModalTitle="Invalid Destinations"
        AlertModalMessage={destValidationError}
        AlertModalButtons={[{ Title: "OK", Method: null }]}
      />
    );
  }

  if (guideActionAlert) {
    return (
      <AlertModal
        show={true}
        onHide={() => setGuideActionAlert(null)}
        AlertModalTitle={guideActionAlert.title}
        AlertModalMessage={guideActionAlert.message}
        AlertModalButtons={guideActionAlert.buttons}
      />
    );
  }

  if (googleSearchError !== "") {
    // Search limit: offer "From existing guides" right here — back to the
    // setup step with that option chosen and the destinations kept — but only
    // while nothing has been added in this run (a later destination of a
    // multi-city build hitting the limit would otherwise rebuild the earlier
    // ones). 2026-10-16.
    const canSwitchToGuides = searchLimitHit && runAddedPlaceIdsRef.current.size === 0;
    return (
      <AlertModal
        show={true}
        onHide={() => {
          setGoogleSearchError("");
          setSearchLimitHit(false);
          if (switchToGuidesRef.current) {
            switchToGuidesRef.current = false;
            return;
          }
          closeWizard();
        }}
        AlertModalTitle={searchLimitHit ? LIMIT_MESSAGES.search.title : "Search failed"}
        AlertModalMessage={searchLimitHit ? (canSwitchToGuides ? LIMIT_MESSAGES.search.body : LIMIT_MESSAGES.searchLater.body) : googleSearchError}
        AlertModalButtons={searchLimitHit
          ? [
              { Title: "How Andiamo stays free", Method: openPartnerOffers },
              ...(canSwitchToGuides ? [{ Title: "Use existing guides", Method: () => { switchToGuidesRef.current = true; setFromGuides(true); setStep(STEP_SETUP); } }] : []),
              { Title: "OK", Method: null },
            ]
          : [{ Title: "OK", Method: null }]}
      />
    );
  }

  if (thisGuidePick) {
    return (
      <PlacesReviewModal
        groups={thisGuidePick}
        categories={window.guide_obj?.categories}
        user_info={user_info}
        onCancel={() => { copyPendingRef.current = false; setThisGuidePick(null); }}
        onConfirm={(ids) => {
          const forNewGuide = copyPendingRef.current;
          copyPendingRef.current = false;
          setThisGuidePick(null);
          if (ids.length === 0) return;
          if (forNewGuide) startBuild(ids);
          else buildForCurrentGuide(ids);
        }}
      />
    );
  }

  if (showDescribe) {
    return (
      <VoiceTripIntentFlow
        dayStartHour={GuideSettings?.schedule_day_start ?? 9}
        dayEndHour={GuideSettings?.schedule_day_end ?? 20}
        onApply={applyDescribedTrip}
        onCancel={() => setShowDescribe(false)}
      />
    );
  }

  if (showDestinationsScreen) {
    // "From existing guides": the pickers offer only countries and cities a
    // guide exists for (see guideCitiesByCountry). Wait for the guide list —
    // showing the full lists for a moment would defeat the point.
    if (fromGuides && guideList === null) {
      return (
        <div className="ngw-pill">
          <span className="ngw-pill-text">Loading the guides you can build from…</span>
        </div>
      );
    }
    return <DestinationsScreen onHide={closeDestinationsScreen} suppressFindPlacesOffer guideCities={fromGuides ? guideCitiesByCountry() : null} datesOnly={!fromGuides && placeSource === "thisguide"} />;
  }

  if (showPreferences) {
    return <PreferencesScreen onHide={() => setShowPreferences(false)} />;
  }

  if (step === STEP_AI_SEARCH) {
    const dest = currentDestRef.current;
    // Same aiPreferences fields TripAI.js's formQuestion() folds into its
    // own prompt — this is the actual personalization "customize to your
    // preferences" promises, previously never read here at all.
    const prefs = window.guide_obj?.aiPreferences || {};
    const prefsClause = [
      prefs.TripType,
      prefs.travelingWith,
      prefs.selectedBudget,
      prefs.selectedPace,
      prefs.selectedTransportation,
    ].filter(Boolean).join(" ");
    const autoQuestion = `Top 20 attractions to visit in ${dest.city} ${dest.country}`
      + (prefsClause ? ` for a ${prefsClause} trip` : "")
      + (prefs.focusOn ? `. Focus on ${prefs.focusOn}` : "")
      + (prefs.Activities ? `. Include the following activities ${prefs.Activities}` : "")
      + (prefs.selectedAccessibility ? `. Must be accessible for a traveler with ${prefs.selectedAccessibility === "wheelchair accessible" ? "wheelchair accessibility needs — avoid places or routes with stairs or no elevator/ramp access" : "limited mobility — avoid places or routes requiring extensive walking or stairs"}` : "");
    // A cache hit for a no-preference search is handled entirely by the
    // effect above — never mount the live AISearch component in that case
    // (it would fire a real LLM call on mount regardless of whether we end
    // up using its result). Same pill STEP_GOOGLE_SEARCH shows while it
    // works; the effect advances the step again almost immediately.
    if (!prefsClause && !prefs.focusOn && !prefs.Activities && !prefs.selectedAccessibility
      && getCachedSearchResults(dest.city, dest.country)) {
      return (
        <div className="ngw-pill">
          <span className="ngw-pill-text">Searching for places in {dest.city}…</span>
        </div>
      );
    }
    return (
      <AISearch
        // Forces a fresh instance per destination — without this, two
        // consecutive AI-sourced destinations would reuse the same mounted
        // AISearch (same component type/position), so its own internal
        // search never restarts for the second one.
        key={`ai-search-${destRunToken}`}
        show={true}
        onHide={handleAISearchHide}
        user_info={user_info}
        update_ui={updateUI}
        set_schedule_name={SetScheduleName}
        find_search_result={FindSearchResult}
        add_search_result={AddSearchResult}
        questions={AppSettings.AISearch2}
        ai_categories={AppSettings.AICategories}
        ai_base_categories={AppSettings.AIBaseCategories}
        search_results_names={SearchResultsPlacesList.map((p) => p.Name)}
        autoQuestion={autoQuestion}
      />
    );
  }

  if (step === STEP_GOOGLE_SEARCH) {
    return (
      <div className="ngw-pill">
        <span className="ngw-pill-text">
          {scheduleOptionsRef.current?.placeSource === "thisguide"
            ? `Scheduling this guide's places in ${currentDestRef.current?.city}…`
            : scheduleOptionsRef.current?.placeSource === "ai" && aiTripRef.current
            ? aiStatusText || "Choosing places for your trip…"
            : scheduleOptionsRef.current?.guideSources?.[cityKey(currentDestRef.current?.city, currentDestRef.current?.country)]?.length
            ? `Taking places from your guide for ${currentDestRef.current?.city}…`
            : `Searching for places in ${currentDestRef.current?.city}…`}
        </span>
      </div>
    );
  }

  if (step === STEP_PICKING) {
    return (
      <div className="ngw-pill">
        <span className="ngw-pill-text">
          Pick your places, then tap here when you're done
          {googleSearchElapsedMs != null ? ` (found in ${(googleSearchElapsedMs / 1000).toFixed(1)}s)` : ""}
        </span>
        <button className="ngw-pill-done" onClick={addAllAndFinish} title="Skip picking — add every result and build the schedule">
          Add All &amp; Continue
        </button>
        <button className="ngw-pill-done" onClick={finishPicking}>
          Done
        </button>
        <button className="ngw-pill-close" onClick={closeWizard} title="Skip">
          <FontAwesomeIcon icon={solid("xmark")} />
        </button>
      </div>
    );
  }

  if (step === STEP_SETUP) {
    const scheduleNames = window.guide_obj?.schedule?.geScheduleNames() || [];
    // "From existing guides" with no guide chosen for any destination would
    // quietly run as an ordinary search for every one — not what was asked
    // for. At least one destination needs a guide; the others may still
    // fall back to a search (each row says so).
    const guidesChosen = destinations.filter((d) => d.city && d.country && !d.noSearch && (guideSources[cityKey(d.city, d.country)] || []).length > 0).length;
    const canCreate = destinations.length > 0 && destinations.every((d) => d.country !== "" && d.city !== "" && d.dateEnd >= d.dateStart)
      && (!fromGuides || guidesChosen > 0);
    const defaultName = destinations.length === 1
      ? `${destinations[0].city}${destinations[0].country ? ", " + destinations[0].country : ""} Trip`
      : `${destinations[0]?.country || ""} Trip`;
    return (
      <div className="modal">
        <Modal show={true} onHide={onHideGuarded} dialogClassName="tai-dialog" aria-labelledby="ngw-setup-title" centered scrollable={false}>
          <Modal.Body style={{ padding: 0 }}>
            <div className="tai-shell">
              <div className="tai-header">
                <div className="tai-header-icon">
                  <FontAwesomeIcon icon={solid("wand-magic-sparkles")} />
                </div>
                <h2 className="tai-title" id="ngw-setup-title">Build My Trip</h2>
                <button className="tai-close" onClick={onHideGuarded}>
                  <FontAwesomeIcon icon={solid("xmark")} />
                </button>
              </div>

              <div className="tai-scroll">
                {/* Where the places come from, and (separately) whether to
                    review them — 2026-10-16. Was one question mixing both
                    ("Auto-pick / Let me choose / From existing guides"), with
                    AI hidden under advanced options. Each source shows only
                    its own options below. */}
                <span className="tai-section-label">Where should places come from?</span>
                {/* Source tiles (2026-10-17): a 2×2 grid instead of four radio
                    rows keeps every source visible (the point of the
                    consolidation) in less height; each tile has a short
                    subtitle (hidden on narrow screens, where it made each tile
                    three lines) and the chosen one's full description sits
                    under the grid. */}
                {(() => {
                  const n = thisGuideEligible().length;
                  const sources = [
                    { key: "search", icon: solid("magnifying-glass"), label: "Search", sub: "Real places nearby", on: !fromGuides && placeSource === "google",
                      desc: "Top places found near each destination, scheduled around their opening hours." },
                    ...(n > 0 ? [{ key: "thisguide", icon: solid("location-dot"), label: "This guide", sub: `${n} place${n === 1 ? "" : "s"} already here`, on: !fromGuides && placeSource === "thisguide",
                      desc: `This guide's ${n} place${n === 1 ? "" : "s"}, matched to its destinations by city.` }] : []),
                    { key: "guides", icon: solid("book-open"), label: "Guides", sub: "Yours or Featured", on: fromGuides,
                      desc: "Places from one of your guides or our Featured guides for each destination." },
                    { key: "ai", icon: solid("wand-magic-sparkles"), label: "AI", sub: "Picks for your trip", on: !fromGuides && placeSource === "ai",
                      desc: "AI chooses places to match your preferences; the schedule is built around opening hours." },
                  ];
                  const chosen = sources.find((s) => s.on);
                  return (<>
                    <div className={`bmt-source-grid${sources.length % 2 ? " odd" : ""}`} role="radiogroup" aria-label="Where should places come from?">
                      {sources.map((s) => (
                        <button key={s.key} type="button" role="radio" aria-checked={s.on} className={`bmt-source-tile${s.on ? " on" : ""}`} onClick={() => chooseSource(s.key)}>
                          <FontAwesomeIcon icon={s.icon} className="bmt-source-icon" />
                          <span className="bmt-source-text">
                            <span className="bmt-source-label">{s.label}</span>
                            <span className="bmt-source-sub">{s.sub}</span>
                          </span>
                        </button>
                      ))}
                    </div>
                    {chosen && <p className="bmtw-dest-hint bmt-source-desc">{chosen.desc}</p>}
                  </>);
                })()}
                <div className="tai-check-row" onClick={() => setPickPlacesAuto(!pickPlacesAuto)}>
                  <div className={`tai-check-box${!pickPlacesAuto ? " checked" : ""}`}>
                    {!pickPlacesAuto && <FontAwesomeIcon icon={solid("check")} />}
                  </div>
                  <span className="tai-check-label">Let me review and choose the places</span>
                </div>

                {/* "Describe your trip" (2026-10-16): type or speak the trip;
                    VoiceTripIntentFlow (parse_trip_intent.php) fills the
                    destinations and AI preferences and the source switches to
                    AI — everything stays editable below. Replaces the link
                    that handed off to the separate Build Trip with AI dialog. */}
                {!(placeSource === "thisguide" && !fromGuides) && (<>
                <button className="tai-paste-dest-btn vtif-entry-btn" onClick={() => setShowDescribe(true)}>
                  <FontAwesomeIcon icon={solid("wand-magic-sparkles")} />
                  Describe your trip (type or speak)
                </button>
                <p className="bmtw-dest-hint tai-voice-or-manual-hint">
                  Or fill in the destinations and options below yourself.
                </p>
                </>)}


                <span className="tai-section-label">Destinations</span>
                {!fromGuides && placeSource === "thisguide" && destinations.length > 0 && (() => {
                  const { outside } = thisGuidePlan();
                  return (
                    <p className="bmtw-dest-hint">
                      {suggestedFromPlaces
                        ? `Suggested from this guide's ${thisGuideEligible().length} places — check the dates.`
                        : "Using this guide's destinations — its places are matched to them by city."}
                      {outside.length > 0 ? ` ${outside.length} place${outside.length === 1 ? " isn't" : "s aren't"} near any of them (${outside.slice(0, 3).map((p) => p.Name).join(", ")}${outside.length > 3 ? "…" : ""}) and won't be used.` : ""}
                    </p>
                  );
                })()}
                {destinations.length > 0 && <p className="bmtw-dest-hint">Check where and when you're going. Tap to change.</p>}
                {destinations.length === 0 ? (
                  effectiveDestinations().some((d) => d.city && d.dateStart && d.dateEnd && d.dateStart.equals(d.dateEnd)) ? (
                    // destinations here is placedDestinationsFromGuide's filtered
                    // view, which silently drops a destination whose dateStart
                    // equals dateEnd (zero duration) — the guide's raw list is
                    // checked so it doesn't look like it was never added.
                    // Found 2026-09-27.
                    <div className="bmtw-dest-callout" onClick={() => setShowDestinationsScreen(true)}>
                      <FontAwesomeIcon icon={solid("triangle-exclamation")} />
                      <span>One of your destinations has the same start and end date/time — tap to give it a real date range.</span>
                    </div>
                  ) : null
                  // No "get started" prompt when empty (2026-10-17): the
                  // "Add destinations" button below covers it, and the dialog
                  // offers other ways in above (Describe your trip, a source
                  // that suggests destinations).
                ) : (
                  <div className="bmtw-dest-card" onClick={() => setShowDestinationsScreen(true)} title="Change destinations or dates">
                    {destinations.map((dest) => (
                      <div key={dest.id} className="bmtw-dest-readonly">
                        <span className="bmtw-dest-readonly-place">{dest.city}, {dest.country}</span>
                        <span className="bmtw-dest-readonly-dates">
                          {dest.dateStart.toFormat("MMM d")} – {dest.dateEnd.toFormat("MMM d, yyyy")}
                        </span>
                        {replacesLabel(dest) && (
                          <span style={{ marginLeft: 6, fontSize: 12, color: "#D97706", whiteSpace: "nowrap" }}
                            title="Its old events in the schedule are replaced when it's built">
                            <FontAwesomeIcon icon={solid("rotate")} /> {replacesLabel(dest)}
                          </span>
                        )}
                        {isInTrip(dest) && (
                          <span role="button" tabIndex={0} style={{ marginLeft: 6, cursor: "pointer", whiteSpace: "nowrap" }}
                            onClick={(e) => { e.stopPropagation(); if (skipBuilt) toggleRedo(dest.id); }}
                            onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && skipBuilt) { e.preventDefault(); e.stopPropagation(); toggleRedo(dest.id); } }}
                            title={willRedo(dest) ? "Will be built again, replacing its events" + (skipBuilt ? " — tap to keep it" : "") : "Already in this trip — tap to redo it"}>
                            {willRedo(dest)
                              ? <><FontAwesomeIcon icon={solid("rotate")} style={{ color: "#D97706" }} /> <span style={{ fontSize: 12, color: "#D97706" }}>Redo</span></>
                              : <FontAwesomeIcon icon={solid("circle-check")} style={{ color: "#16A34A" }} />}
                          </span>
                        )}
                        <FontAwesomeIcon icon={solid("pencil")} className="bmtw-dest-pencil" />
                      </div>
                    ))}
                  </div>
                )}
                <button className="bmtw-dest-edit-btn" onClick={() => setShowDestinationsScreen(true)}>
                  <FontAwesomeIcon icon={solid("map-location-dot")} />
                  {destinations.length === 0
                    ? fromGuides ? "Choose destinations (cities with a guide)" : "Add destinations"
                    : !fromGuides && placeSource === "thisguide" ? "Change dates" : "Change destinations or dates"}
                </button>

                <div className="tai-divider" />

                {/* Where the trip is saved comes before the source's own
                    options (2026-10-17): it's the bigger decision. */}
                {/* This guide's places can go into a new guide too (2026-10-17):
                    someone reading a Featured guide makes their own trip from
                    it. Not "Replace this guide" — that would delete the places
                    the trip is built from. */}
                {(() => { const thisGuideSrc = !fromGuides && placeSource === "thisguide"; return (<>
                <span className="tai-section-label">Where should the trip be saved?</span>
                <div className="tai-check-row" onClick={() => selectGuideTarget("new")}>
                  <div className={`tai-check-box tai-check-radio${guideTarget === "new" ? " checked" : ""}`}>
                    {guideTarget === "new" && <FontAwesomeIcon icon={solid("check")} />}
                  </div>
                  <span className="tai-check-label">{thisGuideSrc ? "A new guide, with copies of these places" : "A new guide"}</span>
                </div>
                {guideTarget === "new" && (
                  <div className="tai-input-row">
                    <span className="tai-input-label">Guide Name</span>
                    <input
                      className="tai-input-field"
                      type="text"
                      value={newGuideName}
                      placeholder={defaultName}
                      onChange={(e) => setNewGuideName(e.target.value)}
                      maxLength={60}
                    />
                  </div>
                )}
                {guideTarget === "new" && isGuestUser() && (
                  <div className="tai-inline-note">
                    Sign in to save this trip — you can keep building it now.{" "}
                    <span
                      className="tai-inline-link"
                      onClick={() => window.dispatchEvent(new CustomEvent("openLoginModal", { detail: { mode: "Login" } }))}
                    >
                      Sign In
                    </span>
                  </div>
                )}
                <div className="tai-check-row" onClick={() => selectGuideTarget("add")}>
                  <div className={`tai-check-box tai-check-radio${guideTarget === "add" ? " checked" : ""}`}>
                    {guideTarget === "add" && <FontAwesomeIcon icon={solid("check")} />}
                  </div>
                  <span className="tai-check-label">Add to this guide</span>
                </div>
                {/* Directly under "Add to this guide", indented (2026-10-22): it
                    only applies there, and belongs to it rather than to
                    "Which schedule?". */}
                  {guideTarget === "add" && !scheduleTarget.startsWith(OVERWRITE_PREFIX) && destinations.some(isInTrip) ? (
                  <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, margin: "2px 0 8px 30px", cursor: "pointer" }}>
                    <input type="checkbox" checked={skipBuilt} onChange={(e) => setSkipBuilt(e.target.checked)} />
                    Skip destinations already in this trip (<FontAwesomeIcon icon={solid("circle-check")} style={{ color: "#16A34A" }} />)
                  </label>
                  ) : null}
                  {guideTarget === "add" && !scheduleTarget.startsWith(OVERWRITE_PREFIX) && destinations.some(isInTrip) ? (
                    <p className="tai-paste-hint" style={{ margin: "0 0 8px 30px" }}>
                      {skipBuilt
                        ? <>Tap a <FontAwesomeIcon icon={solid("circle-check")} style={{ color: "#16A34A" }} /> above to redo just that destination; its events are replaced.</>
                        : "Every destination already in this trip is built again, replacing its events."}
                    </p>
                  ) : null}
                {!thisGuideSrc && (
                <div className="tai-check-row" onClick={() => selectGuideTarget("replace")}>
                  <div className={`tai-check-box tai-check-radio${guideTarget === "replace" ? " checked" : ""}`}>
                    {guideTarget === "replace" && <FontAwesomeIcon icon={solid("check")} />}
                  </div>
                  <span className="tai-check-label">Replace this guide</span>
                </div>
                )}

                </>); })()}
                {guideTarget === "add" && (<>
                  <span className="tai-section-label">Which schedule?</span>
                  <div className="tai-check-row" onClick={() => onScheduleTargetChange(NEW_SCHEDULE_VALUE)}>
                    <div className={`tai-check-box tai-check-radio${scheduleTarget === NEW_SCHEDULE_VALUE ? " checked" : ""}`}>
                      {scheduleTarget === NEW_SCHEDULE_VALUE && <FontAwesomeIcon icon={solid("check")} />}
                    </div>
                    <span className="tai-check-label">Create a new schedule</span>
                  </div>
                  {scheduleTarget === NEW_SCHEDULE_VALUE && (
                    <div className="tai-input-row">
                      <span className="tai-input-label">Name</span>
                      <input
                        className="tai-input-field"
                        type="text"
                        value={newScheduleName}
                        onChange={(e) => setNewScheduleName(e.target.value)}
                        placeholder={defaultName}
                        maxLength={60}
                      />
                    </div>
                  )}
                  {/* Add to / Overwrite + one schedule dropdown (2026-10-17):
                      one radio pair per schedule made 2×N rows. scheduleTarget
                      keeps its old values ("Name" / OVERWRITE_PREFIX + "Name"). */}
                  {scheduleNames.length > 0 && (() => {
                    const overwriting = scheduleTarget.startsWith(OVERWRITE_PREFIX);
                    const chosenName = scheduleTarget === NEW_SCHEDULE_VALUE ? null : overwriting ? scheduleTarget.slice(OVERWRITE_PREFIX.length) : scheduleTarget;
                    const nameFor = () => chosenName || (scheduleNames.find((s) => s.value === "Main") || scheduleNames[0]).value;
                    const modes = [
                      { key: "add", label: "Add to a schedule", on: !!chosenName && !overwriting, value: () => nameFor() },
                      { key: "overwrite", label: "Overwrite a schedule", on: overwriting, value: () => OVERWRITE_PREFIX + nameFor() },
                    ];
                    return (<>
                      {modes.map((m) => (
                        <div key={m.key} className="tai-check-row" onClick={() => !m.on && onScheduleTargetChange(m.value())}>
                          <div className={`tai-check-box tai-check-radio${m.on ? " checked" : ""}`}>
                            {m.on && <FontAwesomeIcon icon={solid("check")} />}
                          </div>
                          <span className="tai-check-label">{m.label}</span>
                        </div>
                      ))}
                      {chosenName && (
                        <div className="tai-input-row">
                          <select
                            className="tai-select"
                            aria-label={overwriting ? "Schedule to overwrite" : "Schedule to add to"}
                            value={chosenName}
                            onChange={(e) => onScheduleTargetChange(overwriting ? OVERWRITE_PREFIX + e.target.value : e.target.value)}
                          >
                            {scheduleNames.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                          </select>
                        </div>
                      )}
                    </>);
                  })()}
                  {scheduleTarget.startsWith(OVERWRITE_PREFIX) && (
                    <p className="tai-paste-hint">Everything currently in "{scheduleTarget.slice(OVERWRITE_PREFIX.length)}" is removed first, then the trip is built into it.</p>
                  )}
                  {scheduleTarget !== NEW_SCHEDULE_VALUE && !scheduleTarget.startsWith(OVERWRITE_PREFIX) && (
                    <p className="tai-paste-hint">Every destination's dates above were shifted to start the day after "{scheduleTarget}" currently ends.</p>
                  )}
                  <div className="tai-check-row" onClick={() => setUpdateItinerary(!updateItinerary)}>
                    <div className={`tai-check-box${updateItinerary ? " checked" : ""}`}>
                      {updateItinerary && <FontAwesomeIcon icon={solid("check")} />}
                    </div>
                    <span className="tai-check-label">Update itinerary to match the schedule</span>
                  </div>
                </>)}

                {!fromGuides && placeSource === "ai" && (
                  <div className="tai-card">
                    <div className="tai-row-item" onClick={() => setShowPreferences(true)}>
                      <span className="tai-row-key">
                        {(() => {
                          const p = window.guide_obj?.aiPreferences;
                          const parts = p && [p.TripType, p.travelingWith, p.focusOn, p.Activities, p.selectedBudget, p.selectedPace, p.selectedTransportation, p.selectedAccessibility].filter(Boolean);
                          return parts && parts.length > 0 ? `Preferences: ${parts.join(" · ")}` : "Preferences: not set";
                        })()}
                      </span>
                      <span className="tai-row-value">Edit</span>
                      <span className="tai-row-chevron"><FontAwesomeIcon icon={solid("chevron-right")} /></span>
                    </div>
                    {/* Labels say what each does (2026-10-17): "Include" /
                        "Only these" / "Exclude" read as three parallel modes.
                        "only these" sits on the "Must include" label's
                        own line, so it's plainly about that list (ignored
                        while the list is empty). */}
                    <div className="bmt-ai-field">
                      <div className="bmt-ai-field-head">
                        <label className="tai-input-label" htmlFor="bmt-ai-include">Must include</label>
                        <div className="bmt-ai-only" role="checkbox" aria-checked={aiIncludeOnly} tabIndex={0}
                          onClick={() => setAiIncludeOnly(!aiIncludeOnly)}
                          onKeyDown={(e) => { if (e.key === " " || e.key === "Enter") { e.preventDefault(); setAiIncludeOnly(!aiIncludeOnly); } }}>
                          <div className={`tai-check-box${aiIncludeOnly ? " checked" : ""}`}>
                            {aiIncludeOnly && <FontAwesomeIcon icon={solid("check")} />}
                          </div>
                          <span>only these</span>
                        </div>
                      </div>
                      <input id="bmt-ai-include" className="bmt-ai-input" type="text" value={aiInclude} placeholder="e.g. a cooking class, the Uffizi" onChange={(e) => setAiInclude(e.target.value)} />
                    </div>
                    <div className="bmt-ai-field">
                      <div className="bmt-ai-field-head">
                        <label className="tai-input-label" htmlFor="bmt-ai-skip">Skip</label>
                      </div>
                      <input id="bmt-ai-skip" className="bmt-ai-input" type="text" value={aiExclude} placeholder="e.g. churches, shopping" onChange={(e) => setAiExclude(e.target.value)} />
                    </div>
                  </div>
                )}
                {fromGuides && <span className="tai-section-label">Guide for each destination</span>}
                {fromGuides && (
                  <div className="tai-card bmt-guide-slots">
                    {destinations.filter((d) => d.city && d.country && !d.noSearch).length === 0 && (
                      <p className="tai-paste-hint">Add a destination first, then choose a guide for it.</p>
                    )}
                    {destinations.filter((d) => d.city && d.country && !d.noSearch).map((d) => {
                      const key = cityKey(d.city, d.country);
                      const chosen = guideSources[key] || [];
                      const available = guidesForDestination(d).length;
                      return (
                        <div key={d.id ?? key} className="bmt-guide-slot">
                          <div className="bmt-guide-slot-text">
                            <span className="bmt-guide-slot-city">{d.city}, {d.country}</span>
                            <span className="bmt-guide-slot-status">
                              {chosen.length > 0
                                ? chosen.map((g) => g.guide_name).join(" + ")
                                : guideList === null
                                ? "Checking for guides…"
                                : available > 0
                                ? `${available} guide${available === 1 ? "" : "s"} available — choose one, or this destination will search instead`
                                : "No guide found — will search instead"}
                            </span>
                          </div>
                          {chosen.length > 0 && (
                            <button className="tai-action-btn" onClick={() => setGuideSources((prev) => { const next = { ...prev }; delete next[key]; return next; })}>Clear</button>
                          )}
                          <button className="tai-action-btn" onClick={() => setGuidePickerFor(d)}>{chosen.length > 0 ? "Add another" : "Choose guide"}</button>
                        </div>
                      );
                    })}
                    {guidesChosen === 0 && destinations.some((d) => d.city && d.country && !d.noSearch) && (
                      <p className="tai-paste-hint bmt-guide-needed">Choose a guide for at least one destination to continue.</p>
                    )}
                    <p className="tai-paste-hint">Places come from the guide in its own order of importance; whatever doesn't fit the days is added unscheduled.</p>
                  </div>
                )}

                {/* Pace directly controls how busy the resulting schedule
                    feels — real trip feedback was that it read as buried
                    "advanced" tuning when it's actually a primary, everyday
                    decision, so it's promoted out of "Show advanced options"
                    to always be visible. Ported to mobile the same day. */}
                <span className="tai-section-label">Pace</span>
                <div className="tai-input-row">
                  <select className="tai-select" value={paceMultiplier} onChange={(e) => setPaceMultiplier(e.target.value)}>
                    {PACE_OPTIONS.map((p) => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                </div>

                {/* With the trip options, not the AI's place choices
                    (2026-10-17): it shapes the days like Pace — and stands in
                    for "Leave room for lunch", which the AI source hides. */}
                {placeSource === "ai" && !fromGuides && (<>
                  <span className="tai-section-label">Suggest restaurants for</span>
                  {["breakfast", "lunch", "dinner"].map((m) => (
                    <div key={m} className="tai-check-row" onClick={() => setAiMeals((prev) => ({ ...prev, [m]: !prev[m] }))}>
                      <div className={`tai-check-box${aiMeals[m] ? " checked" : ""}`}>
                        {aiMeals[m] && <FontAwesomeIcon icon={solid("check")} />}
                      </div>
                      <span className="tai-check-label">{m[0].toUpperCase() + m.slice(1)}</span>
                    </div>
                  ))}
                </>)}

                <button className="tai-advanced-toggle" onClick={() => setShowAdvanced(!showAdvanced)}>
                  <FontAwesomeIcon icon={showAdvanced ? solid("chevron-up") : solid("chevron-down")} />
                  {showAdvanced ? "Hide advanced options" : "Show advanced options"}
                </button>

                {showAdvanced ? (
                  <>
                    <span className="tai-section-label">Departure padding</span>
                      <span className="tai-check-label">Minutes before date end to stop scheduling activities.</span>
                    <div className="tai-input-row">
                      <input
                        type="number"
                        min="0"
                        className="tai-input"
                        value={departurePaddingMinutes}
                        onChange={(e) => setDeparturePaddingMinutes(e.target.value)}
                      />
                    </div>

                    {!(placeSource === "ai" && !fromGuides) && <div className="tai-check-row" onClick={() => setWantLunch(!wantLunch)}>
                      <div className={`tai-check-box${wantLunch ? " checked" : ""}`}>
                        {wantLunch && <FontAwesomeIcon icon={solid("check")} />}
                      </div>
                      <span className="tai-check-label">Leave room for lunch</span>
                    </div>}

                    <span className="tai-section-label">Add more detail</span>
                    <div className="tai-check-row" onClick={() => setEnrichInfoWanted(!enrichInfoWanted)}>
                      <div className={`tai-check-box${enrichInfoWanted ? " checked" : ""}`}>
                        {enrichInfoWanted && <FontAwesomeIcon icon={solid("check")} />}
                      </div>
                      <span className="tai-check-label">Look up extra info (Wikipedia)</span>
                    </div>
                    <div className="tai-check-row" onClick={() => setEnrichHoursWanted(!enrichHoursWanted)}>
                      <div className={`tai-check-box${enrichHoursWanted ? " checked" : ""}`}>
                        {enrichHoursWanted && <FontAwesomeIcon icon={solid("check")} />}
                      </div>
                      <span className="tai-check-label">Look up opening hours</span>
                    </div>
                  </>
                ) : null}
              </div>

              {guidePickerFor ? (
                <GuideDownload
                  show={true}
                  onHide={() => setGuidePickerFor(null)}
                  user_info={user_info}
                  guide_sort_option={[]}
                  set_guide_sort_option={() => {}}
                  callback={onGuidePicked}
                  mode={"list"}
                  modify_only={"false"}
                  guide_meta={{ guide_id: window.guide_obj?.guide_id }}
                  guide_type={""}
                  initial_guide_type={guidesForDestination(guidePickerFor).some((g) => g.data.type === "Featured") || guidesForDestination(guidePickerFor).length === 0 ? "Featured" : "Mine"}
                  initial_search_text={guidePickerFor.city}
                />
              ) : null}

              <div className="tai-footer">
                <button className="tai-cancel-btn" onClick={onHideGuarded}>Cancel</button>
                <div className="tai-footer-spacer" />
                <button className="tai-primary-btn" disabled={!canCreate} onClick={startBuild}>
                  <FontAwesomeIcon icon={solid("wand-magic-sparkles")} />
                  Create
                </button>
              </div>
            </div>
          </Modal.Body>
        </Modal>
      </div>
    );
  }

  if (step === STEP_ENRICHING) {
    return (
      <div className="ngw-pill">
        <span className="ngw-pill-text">{enrichProgress || "Adding detail…"}</span>
        <button className="ngw-pill-close" onClick={skipEnrichment} title="Skip remaining">
          <FontAwesomeIcon icon={solid("xmark")} />
        </button>
      </div>
    );
  }

  return null;
};

export default BuildMyTripWizard;
